import { AfterViewInit, ChangeDetectorRef, Directive, ElementRef, HostBinding, Input, OnDestroy } from '@angular/core';
import { WizObjectUtils, WIZ_BOOLEAN, WizDateUtils, FILTER_TYPE} from '@wizeoo/akm-core-lib';
import {
  PopUpManager,
  WizDatasource,
  WizIcon,
  WizPopUp,
  WizRoot,
  WizEventManager
} from '@wizeoo/akm-component-lib';
import { BaselineEngine, COMPUTE_MODE, ComputeResult, EM_ACTION, FIELD_KIND, FeiKeyDef, FormulaCoreEngine, LoopResult, Triggers, VisionVO, WidgetCellRenderer, WizConstants } from '@wizeoo/akm-compute-lib';
import { AlkEvent } from '../../../framework/events/alk.event';
import { AggregateEngine } from '../../../manager/computer/aggregate-engine';
import { ExcelSheetVo } from '../../../manager/export/excel-sheet.vo';
import { IWidget, IWidgetFilter, PageEvent } from "../../../manager/widgets/i-widget";
import { Baseline, Measure, VisionStep, WidgetConfigurationItemObj, WidgetConfigurationObj, WidgetSort } from '@wizeoo/akm-message-lib';
import { ALK_EVENT_TYPE, PAGE_EVENT_KIND, WIDGET_ACTION_ORIGIN, WIDGET_GLOBAL_KIND, WIDGET_KIND, WIDGET_STATE } from '../../../model/enums';
import { ViewData } from '../../../model/view.data';
import { FeiVO } from '../../../vo/workingdata/fei.vo';
import { Pivot } from '../pivot/pivot.component';
import { CtxtEntry } from '../../../manager/context/user-context.service';
import { getDefaultWidgetConfigurationObj } from '../../../manager/widgets/widget.engine';
import { Model } from '../../../model/model';
import { ChartsEngine } from '../../../manager/charts/charts.engine';
import { WizColDef } from '../../../manager/workingdata/fei.engine';
import { clone, cloneDeep, isEqual } from 'lodash-es';
import { GridsterWidget } from '../../../vo/workingzone/gridster-widget';
import { WidgetLine } from '@wizeoo/akm-compute-lib';
import { UpdateEntry, UpdateRequest } from '../../../command/core/sync/sync.service';
import { PivotCellEditPopData } from '../../../manager/widgets/i-widget';
import { FreedataValueVO } from  '../../../vo/core/freedatavalue.vo';
import { ComputeCallCommand } from '../../../command/compute/compute-call.cmd';
import { ICellRendererParams } from 'ag-grid-community';
import { FormatVO } from '../../../vo/workingdata/format.vo';

@Directive()
export abstract class WidgetBasic implements IWidget, AfterViewInit, OnDestroy {
  @HostBinding('style.width') width: string = '100%';
  @HostBinding('style.height') height: string = '100%';

  /** gestion de la visibilité */
  @HostBinding('class.invisible-class') get visibility() {
    return this.visible === false;
  }

  @Input() public model: Model = null;


  constructor(public elementRef: ElementRef, public cdRef: ChangeDetectorRef = null) {
  }


  @Input() public visible: boolean = true; // le widget est visible
  @Input() public pivotMode: boolean = false; // mode pivot ou mode simple

  public activeFocus: boolean = false; // j'ai une cellule en cours d'édition

  /** aggrégat véritable en colonnes */
  public get columnAggregate() : boolean {
    return this.context?.c?.length > 0;
  }

  /**
   * @override IWidget
   * le nom unique du composant dans la Tab
   */
  @Input() public uid: string;

  /**
   * @override IWidget
   * lancement en mode configuration (depuis la vue templating)
   */
  @Input() public templatingMode: boolean = false;

  /**
   * @override IWidget
   * contrat d'interface IWidget : retourne la configuration du composant
   */
  @Input() public settings: WidgetConfigurationObj; // la configuration basique de ce composant (issue du template)

  /**
   * @override IWidget
   * nature du widget
   */
  public widgetKind: WIDGET_KIND = null;

  /**
   * @override IWidget
   * nature du widget
   */
  public widgetGlobalKind: WIDGET_GLOBAL_KIND = null;

  /**
   * @override IWidget
   * l'état du widget
   * enums.WIDGET_STATE
   */
  public state: WIDGET_STATE = null; 

  public vision: VisionVO = null; // la vision
  public visionStep: VisionStep = null; // le visionStep
  public scenario: string = null; // le scenario
  
  // les filtres pour ce widget (symboliques)
  public filters: Map<string, IWidgetFilter> = null; 


  // L'instance de FEI manipulée par ce composant
  private _fei: FeiVO = null; // pour IForm... comprends pas
  public get fei(): FeiVO {
    return this._fei;
  }
  public set fei(value: FeiVO) {
    this._fei = value;
  }

  /** fei de niveau inférieur, pour contrôles de cohérence et alimentation  */
  public nextStepFei: FeiVO = null;
  // aucune colonne éditable sur le sous-form
  public zeroColumns: boolean = false; 

  // les mesures de contrôle de cohérence, alimentée lors de la configuration
  protected _orderedControls: Measure[] = null;

  // les mesures de contrôle de cohérence globale (widget.i[])
  protected _integrityControls: Measure[] = null;

  /**
   * datasource
   */
  public mainDS: WizDatasource<WidgetLine> = new WizDatasource<WidgetLine>([])
    .setItemCodeField('key')
    .setSelectionMode('multiple')
    .setFilter(this.mainFilterFunction.bind(this))
    .build();

  /**
   * construction des lignes
   */
  public async buildLines(): Promise<void> {

    // si je suis en mode compute, il faut d'abord les charger
    if(this.model.widgetEngine.widgetIsCompute(this)) {

      let hasWidget: boolean = this.fei.pages?.has(this.uid);
      if(!hasWidget) { // on va compter le nombre de ligne pour alimenter les pages
        await new ComputeCallCommand(this.model).countPagesForWidget(this).execute();
      }
      let requestPage: number = this.fei.requestPage?.get(this.uid) || this.context.cmp || 0;
      let hasPage: boolean = this.fei.pages?.get(this.uid)?.has(requestPage);
      if(!hasPage) { // alimentation de la page sur le FEI
        // la commande va mettre à jour le contexte
        await new ComputeCallCommand(this.model).getPage(this).execute();
      } else { 
        this.context.cmp = requestPage;
        this.updateContext(this.context);
        WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.COMPUTE_GET_RESULT, this));
      }
      
      let lines: WidgetLine[] =  this.fei.pages?.get(this.uid)?.get(this.context.cmp || 0);
      this.fei.lines = new Map();
      lines?.forEach(wl => {
        this.fei.addLine(wl);
      });
      this.fei.workingPoolReady = true;
    }

    const lines = this.model.feiEngine.buildRows(this);
    this.mainDS.setSource(lines); // alimentation de la DS des lignes

    return;
  }

  // pour override  
  public buildFilters(): void {
    this.filters = null; //reset

    if(!!this.context.f) {
      this.filters = new Map();
      this.context.f.forEach((wcio: WidgetConfigurationItemObj) => { 
        if (!!BaselineEngine.getKey(this.fei, wcio.cod) || wcio.cod === ViewData.FILTER_GENERIC  || wcio.cod === ViewData.FILTER_ME) {
          let filter: IWidgetFilter = this.model.widgetEngine.buildFilter(this, wcio);
          let key: string = filter.fkd?.fullCode + filter.wcio.fdvf;
          if(filter.wcio.cod === ViewData.FILTER_GENERIC) { // clé du filtre générique
            key = ViewData.FILTER_GENERIC + filter.wcio.fdvf;
          } else if(filter.wcio.cod === ViewData.FILTER_ME) { // clé du filtre générique
            key = ViewData.FILTER_ME + filter.wcio.fdvf;
          }

          // tag boolean
          if(wcio.fmt) {
            let parts: string[] = wcio.fmt.split(WizConstants.KEY_SEPARATOR);
            let format: FormatVO = Model.getInstance().formatEngine.getFormat(parts[0], parts[1]);
            if(format?.dataKind === FIELD_KIND.BOOLEAN) {
              // console.log("buildFreedataFilter boolan", filter);
              filter.onBoolean = true;
            }
          }

          this.filters.set(key, filter);
        }

      });
    } 
  }

  /**
   * FilterFunction du grid, sur la base des filtres
   * @param item WidgetLine
   * @returns true si visible
   */
  public mainFilterFunction(item: WidgetLine): boolean {

    let result: boolean = true; // on part du principe que çà passe
    let filter: IWidgetFilter; // les filtres qu'on va jouer

    // mode compute, c'est l'appel qui filtre, donc tout est OK
    if(this.model.widgetEngine.widgetIsCompute(this)) {
      return true;
    }


    // contrôle par filtre utilisateur
    if(!this.context.noUserFilter) {
      result = this.model.widgetEngine.doUserFilter(this.fei, item); 
    }

    // fin du contrôle par utilisateur, première chance de rater
    if(!result) { // le filtre global me laisse passer
      // item.isRendered = false;
      return false; // filtré par le filtre global
    }  

    // TODO TODO filtre global sur filter => recalcul des line.pool
    // TODO TODO filtre global sur sous-élément hierarchique => filtrer


    // contrôle par filtre global
    if(!!this.model.widgetEngine.globalFilter) { 
      let globalFilter: IWidget = this.model.widgetEngine.checkGlobalFilter();
      if(globalFilter.filters) { // filtre actifs sur le global
        for (var i = 0, len = globalFilter.filters.size; i < len; i++) {
          filter = [...globalFilter.filters.values()][i];
          if(!!filter.fkd) { // filtre vrai, pas Me ou Generic
            if(this.fei?.keyMap?.has(filter.fkd.fullCode)) { // je ne filtre que sur ce que je possède
              
              if(!!filter.mandatory && !filter.active) { // filtre global obligatoire sans valeur
                result = false;
                // console.log('oups mandatory global void', filter);
                break;
              }

              if (filter.active) {
                if(filter.fkd.isScen) { // filtre global scenar, seulement sur les multiScen
                  if(this.fei.scenario === WizConstants.SCEN_KEY_CODE) {
                    result = this.model.widgetEngine.doFilter(item, filter, this);  
                  }
                } else { // filtres normaux, c'est OK
                  result = this.model.widgetEngine.doFilter(item, filter, this);  
                }
                
                if(!result) {
                  break;
                }
              }
            }
          }
          else {
            // filtre sur MOI
            if(filter.wcio.cod === ViewData.FILTER_ME && filter.active) {
              if(item.bl?.createBy !== this.model.connectedUser.mail) {
                result = false;
              }
              // console.log('this.fei.keyMap', this.fei.keyMap);
              // let meMeasure: Measure = this.fei.keyMap.get()
            }
            // console.log('TODOTODO filtre global Me', filter);
          }
        }
      }
    }

    // fin du contrôle global, première chance de rater
    if(!result) { // le filtre global me laisse passer
      // item.isRendered = false;
      return false; // filtré par le filtre global
    }   

    if(this.filters) { 
      for (var i = 0, len = this.filters.size; i < len; i++) {
        filter = [...this.filters.values()][i];
        if (filter.active) {
          
          // filtre générique, on doit construire la chaine de valeur si elle n'existe pas
          if(filter.wcio.cod === ViewData.FILTER_GENERIC && !item.byWidgetStringMap?.has(this.uid)) {
            this.feedWidgetLineAsString(item);
          }
          
          
          result = this.model.widgetEngine.doFilter(item, filter, this);  
          if(!result) {
            break;
          }
        }
      }
    }

    // item.isRendered = result;
    return result;
  }

  public settingsPopClassFactory: any = null; // le type de popUp pour la configuration
  public settingsPopInstance: any = null; // l'instance de popUp de conf

  ngAfterViewInit() {
    // on alimente l'instance physique (moi) du gridsterWidget dans la map de widgets du WidgetEngine
    let symbolic: GridsterWidget = this.model.widgetEngine.getWidgetByUid(this.uid);
    if(symbolic) { // pas d'inscription pour les sous-widgets dans les popUps
      symbolic.instance = this;
    }
    
    // fin de calcul initial d'un FEI, on vérifie que tout est dispo pour dessiner
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.FEI_COMPUTED, this.checkFeiState);
    // le filtre global est dispo
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.GLOBAL_FILTER_READY, this.checkFeiState);
    // un widget est dispo, c'est peut-être mon master
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.WIDGET_READY, this.checkFeiState);
    // mise à jour d'un filtre du widget
    WizEventManager.addListener(this, this, ALK_EVENT_TYPE.INNER_FILTER_UPDATE, this.innerUpdateFilter);
    // modification du filtre global
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.GLOBAL_FILTER_UPDATE, this.globalFilterUpdate);
    // modification du/des scenario(i) global(ux)
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.GLOBAL_SCENAR_UPDATE, this.globalScenarioUpdate);
    // recalcul d'un FEI, pour refresh du widget (& ajout / suppression lignes)
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.FEI_RECOMPUTED, this.recompute);
    // modification possible du pool de sélection d'un widget, refresh si c'est mon maître
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.WIDGET_UPDATED, this.checkMasterSelectionUpdate);
    // demande de suppression en masse
    WizEventManager.addListener(this, this, ALK_EVENT_TYPE.MASS_DELETE_REQUEST, this.massDeleteRequest);
    // sort custom
    WizEventManager.addListener(this, this, ALK_EVENT_TYPE.CUSTOM_SORT, this.customSort);

    // compute.count => dessiner la pagination
    // WizEventManager.addListener(this, this, ALK_EVENT_TYPE.COMPUTE_COUNT, this.computeCount); //compteur remote compute 
    // retour compute, on dessine les pages
    WizEventManager.addListener(this, this, ALK_EVENT_TYPE.COMPUTE_GET_RESULT, this.computeDrawPages); // demande de chargement de la page
 
    // compute.computeReset => tout rejouer pour le widget
    WizEventManager.addListener(this, this, ALK_EVENT_TYPE.COMPUTE_REBUILD, this.computeRebuild); // reset remote
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.COMPUTE_UPDATE, this.reloadCompute); // refresh par socket
    // compute.get => chargement d'une page
    WizEventManager.addListener(this, this, ALK_EVENT_TYPE.COMPUTE_GET, this.computeGet); // demande de chargement de la page

    // sur retour de datacommit, synchro immédiate des BLVs des grids remote pour éviter les pertes de sync sur numRev
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.COMPUTE_FAST_SYNC, this.computeFastSync);
  }

  ngOnDestroy() {
    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.FEI_COMPUTED);
    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.GLOBAL_FILTER_READY);
    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.WIDGET_READY);
    WizEventManager.removeListener(this, this, ALK_EVENT_TYPE.INNER_FILTER_UPDATE);
    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.GLOBAL_FILTER_UPDATE);
    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.GLOBAL_SCENAR_UPDATE);
    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.FEI_RECOMPUTED);
    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.WIDGET_UPDATED);
    WizEventManager.removeListener(this, this, ALK_EVENT_TYPE.MASS_DELETE_REQUEST);
    WizEventManager.removeListener(this, this, ALK_EVENT_TYPE.CUSTOM_SORT);
    WizEventManager.removeListener(this, this, ALK_EVENT_TYPE.COMPUTE_GET_RESULT); 
    WizEventManager.removeListener(this, this, ALK_EVENT_TYPE.COMPUTE_REBUILD);
    WizEventManager.removeListener(this, this, ALK_EVENT_TYPE.COMPUTE_UPDATE);
    WizEventManager.removeListener(this, this, ALK_EVENT_TYPE.COMPUTE_GET);
    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.COMPUTE_FAST_SYNC);
  }


  /**
   * modification des valeurs de filtre du pivot
   * @param e AlkEvent de type INNER_FILTER_UPDATE data = iWidgetFilter
   */
  protected innerUpdateFilter(e: AlkEvent): void {
    // console.log('innerUpdateFilter', e.data, this.context.f);

    let filter: IWidgetFilter = e.data; // le filtre modifié

    // on rince les données volatiles
    this.model.widgetEngine.refreshFilter(filter); 

    // son équivalent dans le contexte
    const filterMap: Map<string, WidgetConfigurationItemObj> = new Map(this.context.f.map((item) => [item.cod + item.fdvf, item]));
    
    let filterKey: string = filter.wcio.cod + filter.wcio.fdvf;
    this.filters.set(filterKey, filter); // mise à jour de la map du widget, perte de l'info sur les FDGrids
    if (filter.active) { // seulement si le filtre est actif
      if(filter.fkd?.datakind === FIELD_KIND.LIST) {
        filterMap.get(filterKey).sel = filter.values?.size > 0 ? [...filter.values].join(ViewData.DOUBLEKEY_SEPARATOR) : null;
      } else {
        filterMap.get(filterKey).sel = filter.values?.size > 0 ? [...filter.values].join(ViewData.KEY_SEPARATOR) : null;
      }
    
      filterMap.get(filterKey).filtExp = filter.regex;
      
    } else {
      filterMap.get(filterKey).sel = null; // null car '' signifie qu'il y a quand même un filtre (=> filtre sur les valeurs vides)
      filterMap.get(filterKey).filtExp = null;
    }
    
    if (!this.templatingMode) { // persistence uniquement hors du template, sinon, c'est délégué
      this._saveContext();
    } else { // mise à jour du template, pas du contexte
      this.settings = this.context;
      WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.WIDGET_SET_SETTINGS, this));
    }
  }

  /**
   * modif sur filtre global
   * @param e ALK_EVENT_TYPE.GLOBAL_FILTER_UPDATE
   */
  private globalFilterUpdate(e: AlkEvent): void { 
    // console.log('globalFilterUpdate', this.widgetKind, this.state, this.fei?.uniqueName, this.fei?.computed);
    if(this.state >= WIDGET_STATE.READY) {
      this.alterContent(WIDGET_ACTION_ORIGIN.GLOBAL_FILTERING);
    }
  }

  /**
   * modif sur scenario global
   * @param e ALK_EVENT_TYPE.GLOBAL_FILTER_UPDATE
   */
  private globalScenarioUpdate(e: AlkEvent): void { 
    // console.log('globalScenarioUpdate', this.widgetKind, this.state, this.fei?.uniqueName, this.fei?.computed);
    if(this.state >= WIDGET_STATE.READY) {
      this.alterContent(WIDGET_ACTION_ORIGIN.GLOBAL_SCENARIO_UPDATE);
    }
  }

  /* ##############################################################################
                  gestion du contexte
    ############################################################################## */
  /**
   * @override IWidget
   * génération du contexte pour persistence (l'appel est effectué par dataEngine)
   */
  public generateContext(): WidgetConfigurationObj {
    return this.context;
  }

  private contextIsClean: boolean = false; // jusqu'a preuve du contraire...

  /** le contexte de ce composant (user ou new, sur la base de la conf des settings) */
  private _lastKnownContext: WidgetConfigurationObj = null; // pour éviter les maj intempestives
  
  protected _context: WidgetConfigurationObj = null;
  public set context(value: WidgetConfigurationObj) {
    if(!this._lastKnownContext) {
      this._lastKnownContext = cloneDeep(value);
      // {...value};
    }
    this._context = value;
    if(this.state < WIDGET_STATE.INIT) {
      this.state = WIDGET_STATE.INIT; // alimentation de l'état, j'ai un contexte
    }
    
  }
  public get context(): WidgetConfigurationObj {
    return this.checkContextIntegrity(this._context); // contrôle de cohérence entre la conf custom et la conf issue du template
  }

  /** construit un contexte valide pour le widget */
  public generateNewContext(kind: WIDGET_KIND): WidgetConfigurationObj {
    
    // les basiques
    const referenceContext = getDefaultWidgetConfigurationObj(); // on part à vide
    referenceContext.v = this.model.visionCode;
    referenceContext.vs = this.visionStep?.level;
    referenceContext.vsp = ''; // TODO gestion des stepPerimeters
    referenceContext.k = kind ? kind.toString() : null;

    switch(kind) {
      case WIDGET_KIND.CALENDAR:
        referenceContext.pm = false; // mode pivot
      break;
      case WIDGET_KIND.TIMELINE:
        referenceContext.pm = false; // mode pivot
      break;
      case WIDGET_KIND.CHART:
        referenceContext.pm = true; // mode pivot
        referenceContext.ct = ChartsEngine.defaultChartType; // type de chart désiré
      break;
      case WIDGET_KIND.TILE:
      case WIDGET_KIND.FORM:
        referenceContext.pm = false;
      break;
      case WIDGET_KIND.PIVOT:
        referenceContext.pm = this.pivotMode; // mode pivot (ou pas)
        referenceContext.k = this.pivotMode ? WIDGET_KIND.PIVOT : WIDGET_KIND.GRID;       
      break;
      case WIDGET_KIND.LIST:
        referenceContext.pm = true; // mode pivot
        referenceContext.k = this.pivotMode ? WIDGET_KIND.PIVOT : WIDGET_KIND.GRID; // on écrase éventuellement le mode     
      break;
      case WIDGET_KIND.GLOBAL_FILTER:
        referenceContext.pm = false; // mode pivot
      break;
      case WIDGET_KIND.FD_CALENDAR:
      case WIDGET_KIND.FD_FORM:
      case WIDGET_KIND.FD_GRID:
      case WIDGET_KIND.FD_TIMELINE:
        referenceContext.pm = false; // mode pivot
        referenceContext.vs = 0; // on écrase la définition de step
      break;

    }
    return referenceContext;

  }
  
  /** contrôle la présence des champs vitaux sur le widget passé en params */
  public feedContextVital(kind: WIDGET_KIND, referenceContext: WidgetConfigurationObj): void {
    if (!referenceContext.r) { // les rows
      referenceContext.r = [];
    }

    if (!referenceContext.d) { // les datas
      referenceContext.d = [];
    }
    if (!referenceContext.c) { // les columns
      referenceContext.c = [];
    }

    if (!referenceContext.f) { // les filters
      referenceContext.f = [];
    }

    if (!referenceContext.cp) { // les params de chart
      referenceContext.cp = [];
    }
    if(kind === WIDGET_KIND.GLOBAL_FILTER) {
      // console.log('feedContextVital ask', referenceContext.v, referenceContext.vs);
      // console.log('feedContextVital', this.vision.code, this.visionStep.level);
      referenceContext.v = this.vision.code;
      referenceContext.vs = this.visionStep.level;
      referenceContext.eda = false;
      referenceContext.scenOn = true;
      referenceContext.scenX = true;
      referenceContext.scenLbl = false;
      referenceContext.scenUM = true;
      referenceContext.scenCode = ViewData.SCEN_KEY_CODE;
    }

  }

  /**
   * @override IWidget
   * contrôle de cohérence entre le template, la conf user et l'init (si vide)
   * @param value la conf
   * @returns la même corrigée si nécessaire
   */
  public checkContextIntegrity(value: WidgetConfigurationObj): WidgetConfigurationObj {
    // Important optim, ne pas passer dans ce traitement si le contexte n'a pas changer (mettre un flag ?)
    if ( !!this._context && this.contextIsClean) {
      return this._context;
    }
    const settingsContext: WidgetConfigurationObj = this.settings; // prise des paramètres de configuration (template)

  

    let userContext: WidgetConfigurationObj = null; // contexte utilisateur
    if (!this.templatingMode) {
      // uniquement si modifications user autorisées
      // console.log('basic checkContextIntegrity');
      const contextEntry: CtxtEntry = {...this.model.userContextService.getWidget(ViewData.currentTab?.featureCode, this.uid)};
      userContext = {...contextEntry?.data};
    }

    let referenceContext: WidgetConfigurationObj = value;

    let version: number = 0; // on contrôle que la version user n'est pas inférieur au template, sinon on écrase
    if (!referenceContext) {
      // le contexte n'est pas instancié
      if (!!settingsContext) {
        // on prend la conf par défaut
        referenceContext = settingsContext;
        version = !!settingsContext.tv ? settingsContext.tv : 0;
      }
      if (!!userContext) {
        // des params user existent, on vérifie leur version
        if (!!userContext.tv && userContext.tv >= version) {
          referenceContext = userContext;
          version = userContext.tv;
        } else {
          // on monte la version du contexte user, pour update
          userContext = referenceContext; // mieux, on rince
          // userContext.tv = version;
        }
      }
      if (!referenceContext) {
        // toujours rien, on instancie par défaut
        referenceContext = this.generateNewContext(this.widgetKind);
      }
    }

    this.feedContextVital(this.widgetKind, referenceContext);
    this._context = referenceContext;
    this.contextIsClean = true; // on flag le contexte pour ne pas passer sans fin ici (getContext)
    return referenceContext;
  }

  /**
   * méthode de génération du contenu de paramètres
   * @param source les éléments de base pour la construction
   * @param forceEvent, sinon les resizes & édition directe de colonne n'activent pas le bt de la vue template
   */
  public updateContext(source: WidgetConfigurationObj, forceEvent: boolean = false): void {
 
    let hasUpdate: boolean = false;
    if(!!source && !this._lastKnownContext) {
      hasUpdate = true;
    } else if(!!source && !!this._lastKnownContext && !WizObjectUtils.deepEquals(source, this._lastKnownContext)) {
      hasUpdate = true;
    }
      
    this.context = source; // alimentation du contexte local
    this._lastKnownContext = {...source}; // mise à jour du dernier contexte connu

    if(!hasUpdate && !forceEvent) { // pas d'update, et pas issu de resize / filter / édition de header
      return;
    }

    if (!this.templatingMode) { // showSetting = false en mode templating
      // persistence uniquement hors du template, sinon, c'est délégué
      this._saveContext();
    } else {
      this.settings = this.context;
      WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.WIDGET_SET_SETTINGS, this));
    }
    this.contextIsClean = false; // on marque pour passer à nouveau dans checkcontextIntegrity (getContext)
  }

  /** persistence du contexte */
  protected _saveContext() {
    this.model.userContextService.saveWidget(this.uid, ViewData.currentTab?.featureCode, this.generateContext());
  }

  /* ##############################################################################
                    alimentation des données FEI / construction du grid
    ############################################################################### */

  /**
   * @override IWidget : méthode d'alimentation des données du composant
   * Va passer à l'étape de contrôle checkFeiState
   * @param v VisionVO
   * @param vs passe un VisionStepVO
   * @param scenario
   * @param configuration: WidgetConfigurationObj
   */
  public setDatasource(v: VisionVO, vs: VisionStep, scenario: string, configuration: WidgetConfigurationObj ): void {
    
    this.vision = v;
    this.visionStep = vs;
    let feiProto: FeiVO = null; // le prototype à créer
    
    if(!!vs) {

      let computeMode: COMPUTE_MODE = configuration.cm ? configuration.cm as COMPUTE_MODE : COMPUTE_MODE.LOCAL;
      feiProto = this.model.dataEngine.getFei(computeMode, vs.level, scenario) as FeiVO; // on le cherche
      
      if(!feiProto) {
        // TODO TODO neutraliser la collecte des dépendances en cas de mode COMPUTE
        feiProto = this.model.dataEngine.generateFei(v, vs, scenario, this.context); // et on le crée s'il n'existe pas
      } else { // si le proto existe est qu'il est en mode compute alors que je suis en mode local, je force le local
        //let computeMode: COMPUTE_MODE = configuration.cm ? configuration.cm as COMPUTE_MODE : COMPUTE_MODE.LOCAL;
        // if(feiProto.computeMode === COMPUTE_MODE.COMPUTE && computeMode === COMPUTE_MODE.LOCAL) {
        //   // on force le mode local, pour charger les dépendances
        //   // TODO TODO ici passer local au FEI
        //   feiProto = this.model.dataEngine.generateFei(v, vs, scenario, configuration, false, COMPUTE_MODE.LOCAL); // et on le crée s'il n'existe pas
        //   // feiProto.computeMode = COMPUTE_MODE.LOCAL;
        // }
        // feiProto.computeMode = computeMode;
      }
    } else {
      // vide
      this.vision = null;
      this.visionStep = null; // on rince les données
      this.updateContext(null); // on reconstruit un nouveau contexte, vide, et on le persiste. Fin du cycle
    }

    if(feiProto !== this.fei) { // on a un delta
      this.fei = feiProto;

      if(!!this.fei) {
        this.state = WIDGET_STATE.FEI_SET; // alimentation de l'état, j'ai un lien vers un FEI
      }

      let e: AlkEvent = new AlkEvent(ALK_EVENT_TYPE.FEI_SET, this.fei);
      this.checkFeiState(e);
    }
  }
  
  /**
   * contrôle de l'état du FEI 
   * contrôle du master éventuel
   * contrôle du filtre global
   * et on build si tout est OK
   */
  private checkFeiState(e: AlkEvent): void {
    let eventFei: FeiVO = e.data;

    // si le FEI n'est pas calculé, on ne fait rien
    // uniquement pour les baselines, ou les FDs avec lien de baselines pas forcément de FEI sur les FDs
    if(!!this.visionStep && !this.fei?.computed) { 
      // console.log('checkFeiState fei not computed', this.uid, this.fei);
      return;
    }

    // pas encore de setDatasource sur le widget de type BASELINE
    if(this.widgetGlobalKind === WIDGET_GLOBAL_KIND.BASELINE && !this.fei) {
      return;
    }

    // pas encore de setDatasource sur le widget de type FREEDATA, SANS FEI lié
    if(this.widgetGlobalKind === WIDGET_GLOBAL_KIND.FREEDATA && !this.visionStep && this.state < WIDGET_STATE.FEI_COMPUTED) {
      // console.log('FD checkFeiState not set', this, this.state);
      return;
    }

    // mon fei est calculé.. je synchronise mon état (si nécessaire)
    // uniquement pour les baselines. Pour les FDs, c'est le setDatasource qui va gérer ce changement d'état
    // TODO TODO euhh... seulement si c'est MON FEI ?
    if(this.fei === eventFei &&  this.state < WIDGET_STATE.FEI_COMPUTED) {
      this.state = WIDGET_STATE.FEI_COMPUTED;
    }

    if(this.state >= WIDGET_STATE.READY && this.fei?.requiredFei?.get(eventFei.visionStepLevel)?.has(eventFei.scenario)) {
      this.alterContent(WIDGET_ACTION_ORIGIN.REQUIRED_COMPUTED);
      // console.log('mySlave FEI', this.fei.uniqueName, eventFei.uniqueName);  
    }


    let master: IWidget = this.model.widgetEngine.checkFirstMaster(this);
    // si j'ai un master et qu'il n'est pas prêt, on ne fait rien
    if (!!master && master.state !== WIDGET_STATE.SELECTION_COMPLETE) { // j'ai un master, mais il n'est pas prêt
      // console.log('checkFeiState master not ready', this.widgetKind, this.fei.uniqueName);
      return;
    }

    if(this.widgetKind !== WIDGET_KIND.GLOBAL_FILTER) { // on contrôle si le globalFilter est OK... sauf pour le globalFilter
      let globalFilter: IWidget = this.model.widgetEngine.checkGlobalFilter();
      // si j'ai un globalFilter et qu'il n'est pas prêt, on ne fait rien
      if (!!globalFilter && globalFilter.state !== WIDGET_STATE.READY) { // j'ai un globalFilter, mais il n'est pas prêt
        // console.log('checkFeiState global filter not ready', this.uid, this.fei);
        return;
      }
    }

    // on est passé, on peut alimenter
    this.drawContent();
  }


  /**
   * construction 'initiale' des objets graphiques du widget :
   * - sur init véritable
   * - sur modification de la datasource
   * - sur modification de scenario (modifie la DS)
   */
  public drawContent(): void {
    //console.log('FDFORM drawContent', this.state);
    if(this.state >= WIDGET_STATE.READY) { // déjà construit, plus rien a faire
      return;
    }

    // passage à l'état finalisé
    this.state = WIDGET_STATE.READY;
    // console.log('READY', this.widgetKind, this.fei.uniqueName);

    // je suis complet, je signale pour les éventuels esclaves
    // console.log('drawContent Im ready', this.fei.uniqueName);
    this.alterContent(WIDGET_ACTION_ORIGIN.READY);
    WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.WIDGET_READY, this)); 
    
      // sur globalFilter prêt, dispatch d'un event
    if(this.widgetKind === WIDGET_KIND.GLOBAL_FILTER) { 
      WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.GLOBAL_FILTER_READY, this.fei)); 
    }
  }

  /**
   * reconstruction des objets graphiques du widget sur changement de structure : 
   * - modification des filtres qui impactent les colonnes
   * - changement de champs en ligne / colonnes / data
   * - changement de filtre
   * - changement de sélection
   * @param origin 
   */
  public alterContent(origin: WIDGET_ACTION_ORIGIN, data?: any): void {

    // mon pool est modifié, je signale aux esclaves
    // console.log('alterContent', this.widgetKind, this.fei.uniqueName, this.state);
    WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.WIDGET_UPDATED, this)); 

    // altération du filtre global, je signale aux esclaves
    if(this.widgetKind === WIDGET_KIND.GLOBAL_FILTER) { 
      WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.GLOBAL_FILTER_UPDATED, this)); 
    }
  }

  /**
    * actions jouées sur le drawContent, le alterContent, ou en cas de modifs de filtre, de sélection
    * @param origin WIDGET_ACTION_ORIGIN pour cibler qui demande
    * @param data l'objet éventuellement porté
    */
  public drawOrRefresh(origin: WIDGET_ACTION_ORIGIN, data?: any):void {

  }

  /* ##############################################################################
                  gestion des valeurs et des formats
    ############################################################################### */

  /**
   * collecte récursive pour filtre sur pivot gigogne
   * @param line 
   * @returns 
   */
  public getSubTotalPool(line: WidgetLine): WidgetLine[] {
    let pool: WidgetLine[] = [];
    this.recursePool(line, pool);
    return pool;
  }


  /**
   * alimentation récursive du pool d'enfant en mode pivot gigogne
   * @param line 
   * @param pool 
   */
  private recursePool(line: WidgetLine, pool: WidgetLine[]): void {
      if(!!line.ch) {
        [...line.ch?.values()].forEach(wl => {
          this.recursePool(wl, pool);
        }); 
      } else {
        if(!line.isSubTotalLine) {
          pool.push(line);
        } 
      }   
  }

  /**
   * pour une WidgetLine, retourne une collection de sous-lignes éligibles pour une colonne
   * Prend en compte le filtre global ET l'aggrégat en colonne
   * @param line la ligne cible (row)
   * @param column la colonne cible, tenant compte des lignes de header
   * @param single contrôle direct sur la widgetLine. C'est une ligne de détail dans un pivot (avec détail)
   * @returns les lignes éligibles
   */
  public getPool(line: WidgetLine, column: any, single: boolean = false): WidgetLine[] {
    let result: WidgetLine[] = [];
    
    // déjà mémorisé, retour rapide
    if(line.linesByColumn?.has(column.parent)) {
      // console.log('hasCol', column, column.parent);
      return line.linesByColumn.get(column.parent);
    }

    let isSimpleLine: boolean = !line.isTotalLine && !line.isSubTotalLine;

    if(single || isSimpleLine) { // je suis dedans, c'est déjà filtré
      result = [line];
    } else {
      let pool: WidgetLine[] = [];
      if(line.subTotalLevel > 0) { // mode gigogne, il faut les lignes de plus bas niveau
        this.recursePool(line, pool);
      } else {
        if(!!line.ch) {
          pool = [...line.ch.values()];
        }
      }
      result = pool.filter((item) => this.mainFilterFunction(item));
    }

    

    // filtre par champs en colonne simple filtre supplémentaire obj = parents|vals
    const parents: string[] = [];
    let firstParent: any = column.parent;

    if(!(this.context?.c?.length > 0)) { // pas d'aggrégat
      return result;
    }

    // if (!this.pivotMode) { // colonnes custom en mode grid, pas besoin de filtrer
    //   return result;
    // }

    while (firstParent && firstParent.groupId) {
      // console.log('firstParent', firstParent.originalColumnGroup.colGroupDef.groupId);
      parents.push(firstParent.originalColumnGroup.colGroupDef.groupId); // valeur véritable du champ, sans ajout du numéro de colonne
      firstParent = firstParent.parent;
    }

    if (parents.length > 0) {
      result = result.filter((item: WidgetLine) => {
        // chaque item du pool doit correspondre aux valeurs des colonnes parentes.
        let colRes: boolean = true;
        parents.forEach((s: string) => {
          if (!!s) {
            const pField: string = s.split(ViewData.KEY_SEPARATOR)[0];
            const pValue: string = s.split(ViewData.KEY_SEPARATOR)[1];
            const itemValue: string = BaselineEngine.getRawKeyOrValue(this.fei, item, pField); // une seule ligne
            if (pValue !== itemValue) {
              colRes = false;
            }
          } else {
            colRes = false;
          }
        });
        return colRes;
      });
    }

    // alimentation du pool par colonne, pour traitement rapide
    if(!line.linesByColumn) {
      line.linesByColumn = new Map();
    }
    line.linesByColumn.set(column.parent, result);

    return result;
  }

  /**
   * 
   * @param line WidgetLine
   * @param colID 
   * @param value string
   * @param format 
   * @returns true si on a mémorisé un résultat (même null) 
   */
  protected widgetLineCellRendererExist(line: WidgetLine, colID: number, value: string, format: string): boolean {
    let wcr = line.rendererMap?.get(colID);
    let result: boolean = false;
    if(wcr?.value === value) {
      // console.log('widgetLineCellRendererExist', wcr.value, value);
      result = wcr.renderer.get(format || 'undefined');
    }
    return result;
  }

  /**
   * accesseur sur le rendu formaté d'une cellule
   * @param line: WidgetLine
   * @param colID 
   * @param value la valeur qu'on a mémorisé
   * @param format 
   * @returns le résultat, peut être nul
   */
  protected getWidgetLineCellRendererValue(line: WidgetLine, colID: number, value: string, format: string): any {
    let result: any = null;
    let wcr: WidgetCellRenderer = line.rendererMap?.get(colID);
    if(wcr) { // y'a un rendu
      if(wcr.value !== value) { // mais c'est pas la bonne valeur
        line.rendererMap.delete(colID);// on bute
      }
      result = wcr.renderer.get(format || 'undefined');
    }
    return result;
  }

  /**
   * mémorisation du rendu formaté d'une cellule
   * @param line: WidgetLine
   * @param colID 
   * @param value
   * @param format 
   * @param result any
   */
  public setWidgetLineCellRendererValue(line: WidgetLine, colID: number, value: string, format: string, result: any): void {
    if(!line.rendererMap) {
      line.rendererMap = new Map();
    }
    if(!line.rendererMap.has(colID)) {
      line.rendererMap.set(colID, {value, renderer: new Map()});
    }

    line.rendererMap.get(colID).renderer.set(format || 'undefined', result);
  }

  // public widgetLineCellRendererForger(line: WidgetLine, colID: number): void {
  //   line?.rendererMap?.delete(colID);
  // }


  /* ##############################################################################
                    gestion de la popUp d'update
     ############################################################################## */

  /**
   * @override IWidget : ouverture de la popUp de configuration (clic sur l'icone de conf)
   * @param callback fonction de retour lors d'un appel délégué par le templateManager
   */
  public showSettingsPop(): void {
    if (this.fei || this.widgetGlobalKind === WIDGET_GLOBAL_KIND.NONE || this.widgetGlobalKind === WIDGET_GLOBAL_KIND.FREEDATA) {
      const targetPosition: any = { left: '0px', top: '31px' }; // position physique

      if (PopUpManager.exist(this.settingsPopInstance)) {
        // suppression de l'instance existante
        this.settingsPopInstance.hide();
      }

      // ouverture de la pop. Data[fei, context, callback]
      this.settingsPopInstance = new WizPopUp( // la taille sera donnée par le style (calc)
        this.settingsPopClassFactory,
        false,
        false,
        null,
        this.settingsPopClassFactory.width,
        this.settingsPopClassFactory.height,
        this,
        null,
        targetPosition
      ).show();
    }
  }

  /**
   * sur retour de la popUp, source pour traitement
   * et dispatch de l'event
   * @param source les éléments de base pour la construction
   */
  public backFromSettingsPop(source: any): void {
    // TODO TODO  on va contrôler un delta dans le contexte, pour éventuelle reconstruction des lignes
    // passer un param structureDamaged au alterContent
    this.updateContext(source, true);
    this.alterContent(WIDGET_ACTION_ORIGIN.CONFIGURE);
  }

  /* ##############################################################################
                    gestion des sélections
     ############################################################################## */

  /** modification du pool sélectionné, on informe les esclaves */
  public updateComponentSelection(): void {
    // mon pool est modifié, je signale aux esclaves
    WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.WIDGET_UPDATED, this)); 

    // altération du filtre global, je signale aux esclaves
    if(this.widgetKind === WIDGET_KIND.GLOBAL_FILTER) { 
      WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.GLOBAL_FILTER_UPDATED, this)); 
    }
  }

  /**
   * autoriser l'édition ou pas, condition du param Editable sur la construction des colonnes (FEI)
   * @param params les params de la cellule
   * @param popMode boolean, je clic en fait sur le crayon.
   * @param silentMode boolean = false (pour appel depuis renderer Action)
   * @param fastmode pour contrôle de base afin d'afficher le crayon
   * @returns true s'il y a 1 et 1 seul baseline. Sinon, il faut passer par la pop
   */
  public checkBeforeEdit(params: any, popMode: boolean = false, silentMode: boolean = false, fastMode: boolean = false): boolean {
    let result: boolean = false;
    let fkd: FeiKeyDef = params.colDef.fkd;

    // console.log('checkBeforeEdit, fastmode:', fastMode, ' coldef:', fkd.fullCode);

    if (!params.colDef.editable && 
      !(fkd?.datakind === FIELD_KIND.ACTION ||
        fkd?.datakind === FIELD_KIND.FILE ||
        fkd?.datakind === FIELD_KIND.LIST ||
        fkd?.datakind === FIELD_KIND.THRESHOLD ||
        fkd?.datakind === FIELD_KIND.BINARY ||
        fkd?.datakind === FIELD_KIND.WORKFLOW ||
        fkd?.datakind === FIELD_KIND.FILTER ||
        fkd?.datakind === FIELD_KIND.CROSS_FILTER) 
    ) { // pas éditable (sauf les actions, les listes, les binaires, les workflow et les filter)
      return false;
    }

    if (this._context.eda === false) { // widget en lecture seule
      return false;
    }

      if(this.widgetGlobalKind === WIDGET_GLOBAL_KIND.BASELINE) {
        if (!!params.colDef.wcio.ro ) { // mode readOnly
          return false;
        }
      } else {
        if (!params.colDef.wcio.ro ) { // mode éditable, c'est inversé pour les FDGrid
          return false;
        }
      }

    

    if (fkd.datakind === FIELD_KIND.BOOLEAN) {
      // pas d'édition de cellule directe pour les checkBox. C'est un autre event
      return false;
    }

    // ligne simple en regroupement, pas d'affichage de l'édition
    const line: WidgetLine = params.data; // ligne source // ou FDV si FDGRID...
    const colDef: WizColDef = params.colDef; // définition de la colonne
    let isSimpleLine: boolean = !line.isTotalLine && !line.isSubTotalLine;
    if(isSimpleLine && 
    colDef.widget?.pivotMode && colDef.place === 'row') { 
      return false;
    }

    // ligne de cumul sur grid simple, pas éditable
    if(line.isTotalLine && !colDef.widget?.pivotMode) {
      return false;
    }

    // ligne de cumul sur grid avec contenu gigogne, pas éditable
    if(line.isSubTotalLine && !!colDef.widget?.context.pmdex) {
      return false;
    }


    if (!this.checkEditRestrictFromCell(params, silentMode)) { // contrôle d'édition sur la mesure (avant fastMode)
      // console.log('checkEditRestrictFromCell out');
      return false;
    }

    if(fastMode) { // on est passé jusque là, c'est OK pour le crayon
      return true;
    }

    if (params?.colDef?.datakind === FIELD_KIND.COLOR && !params?.event) {
      // pas d'édition de cellule directe pour les checkBox ni les colors. C'est un autre event
      // console.log('lock color', params);
      return false;
    }

    if (fkd.datakind === FIELD_KIND.LIST && !popMode) { // pas d'édition de cellule directe pour les listes
      return false;
    }

    // ancien emplacement
    if (!this.checkEditRestrictFromCell(params, silentMode)) { // contrôle d'édition sur la mesure
      return false;
    }


    let linePool: WidgetLine[];
    if (this.pivotMode) {
      linePool = this.getPool(params.data, params.column, isSimpleLine);
      if (popMode) {
        // demamde de pop, à minima 1 ligne
        result = !!linePool && linePool.length > 0; // a minima 1 données pour ouvrir la pop
      } else { // tentative d'édition directe en masse
        // console.log('direct edit pivot mode', this.context.mem);
        if(this.context!.mem >= 2) {
          // console.log('special mass', this.context.mem);
          result = !!linePool && linePool.length > 0; // au moins une ligne
        } else {
          result = !!linePool && linePool.length === 1; // une unique PL pour édition directe
        }
        
      }
    } else {
      linePool = [params.data];
      result = true; // éditable en mode simple
    }

    if (!!this.fei && !this.fei.canUpdateItem(linePool)) { // aucun droit trouvé (uniquement Baselines)
      result = false;
    }

    return result;
  }

  /**
   * alimentation éventuel des updates sur triggers
   * @param updateRequest 
   * @param updates 
   */
  public checkTriggers(updates: UpdateEntry[]): void {
    let moreUpdates: UpdateEntry[] = [];


    updates.forEach((ue: UpdateEntry) => {
      if(!!ue.baselineValue || ue.baselineOperation === EM_ACTION.UPDATE) { // modif de mesure ou de filterKey
        
        //console.log('checkTriggers', ue.field);
        // détecter les changements de valeurs en cascade, même sur les mesures calculées
        // déclencher les triggers pour les cellules calculées
        
        // les éventuels triggers. Je regarde déjà sur ma cellule modifiée
        let reverseTriggers: Set<Triggers> = this.fei.reverseTriggersMap?.get(ue.field) || new Set();


        // on a besoin de la wl
        let wl: WidgetLine = this.fei.getWidgetLineForKey(ue.baseline.scen, ue.baseline.key);

        // étape 1, on applique le calcul sur la mesure en udpate
        this.fei.model.cellMatrixEngine.addCell(wl.level, wl.key, wl.scen, ue.field, ue.newValue);
        // en cas de filterKey, on impacte la BL
        if(ue.baselineOperation === EM_ACTION.UPDATE) { // modif sur une filterKey, maj de la Bl pour prise en compte de la bonne valeur lors du calcul
          // TODO ne pas faire ça, sinon pas de modif en retour de commit
          // ou pas, ça fonctionne
          BaselineEngine.baselineSetFilterKeyValue(wl.bl, ue.field, ue.newValue);
        }
    
        // étape 2, je recalcul et collecte les mesures modifiées 
        let updatedMeasures: Map<string, [string, string]> = FormulaCoreEngine.checkRecurseTriggers(this.fei, wl) || new Map();

        [...updatedMeasures.keys()].forEach(s => {
          let morrTriggers: Set<Triggers> = this.fei.reverseTriggersMap?.get(s) || new Set();
          [...morrTriggers.values()].forEach(t => {
            reverseTriggers.add(t);
          });
        });

        // étape 3, je joue les triggers
        // console.log('checkTriggers', ue.field, reverseTriggers, updatedMeasures);

        // on rince le cache dexie si l'un des triggers est asynchrone
        if(reverseTriggers?.size > 0) {
          [...reverseTriggers.values()].forEach(trig => {
            if(!!trig.mm?.asyncDeps) {
              // console.log('async deps', trig);
              this.model.cellMatrixEngine.clearDexieMatrixLine(wl);
            }
          });
        };
       

        // on boucle sur les triggers
        if(reverseTriggers?.size > 0) {

            const linePack: WidgetLine[] = [wl];

            [...reverseTriggers.values()].forEach(trig => {
              const meas = trig.fkd.measure;
              // pas de restriction sur les triggers
              // on applique la vrai valeur ? TODO contrôler lock
              let keyPartMap: Map<string, string> = new Map();
              keyPartMap.set(ue.field, ue.newValue);

              // console.log('checkTriggers', keyPartMap);
              let res : boolean = true;

              if (res) { // contrôle d'édition sur la mesure (avant fastMode)
                
                keyPartMap.set(ue.field, ue.newValue);
                const cr: ComputeResult = FormulaCoreEngine.computeMmiDependencies(trig.mm.mmi, wl, this.fei, true, keyPartMap);
                // console.log('edit', trig.mm.mmi.form, cr?.valor, ue.newValue);
                if(cr?.valor?.length > 0) {    
                  moreUpdates.push(this.model.dataEngine.generateUpdate(this.fei, wl, trig.fkd, cr.valor, trig.mm.code));
                } else {
                  moreUpdates.push(this.model.dataEngine.generateUpdate(this.fei, wl, trig.fkd, "", trig.mm.code));
                }
              } else {
                //console.log('no edit');
              }
              
            });

        }

        // this.fei.triggersMap?.forEach((trig: Triggers) => {
        //   if(trig.triggers?.has(ue.field)) {
        //     // on a besoin de la wl
        //     let wl: WidgetLine = this.fei.getWidgetLineForKey(ue.baseline.scen, ue.baseline.key);
                     
        //     this.model.cellMatrixEngine.clearDexieMatrixLine(wl);
        //     // recompute (après le clear de la matrice, babache) pour gestion des modifs en cascade
        //     FormulaCoreEngine.computeLines(this.fei, [wl], true);


        //     const linePack: WidgetLine[] = [wl];
        //     const meas = trig.fkd.measure;
            
        //     // pas de restriction sur les triggers
        //     // on applique la vrai valeur ? TODO contrôler lock
        //     let keyPartMap: Map<string, string> = new Map();
        //     keyPartMap.set(ue.field, ue.newValue);

        //     // console.log('checkTriggers', keyPartMap);
        //     let res : boolean = true;

        //     if (res) { // contrôle d'édition sur la mesure (avant fastMode)
              
        //       keyPartMap.set(ue.field, ue.newValue);
        //       const cr: ComputeResult = FormulaCoreEngine.computeMmiDependencies(trig.mm.mmi, wl, this.fei, true, keyPartMap);
        //       // console.log('edit', trig.mm.mmi.form, cr?.valor, ue.newValue);
        //       if(cr?.valor?.length > 0) {    
        //         moreUpdates.push(this.model.dataEngine.generateUpdate(this.fei, wl, trig.fkd, cr.valor, trig.mm.code));
        //       } else {
        //         moreUpdates.push(this.model.dataEngine.generateUpdate(this.fei, wl, trig.fkd, "", trig.mm.code));
        //       }
        //     } else {
        //       //console.log('no edit');
        //     }

        //   }
        // })
      } 
      // TODO TODO contrôle sur création
      // LMA on passe jamais ici, à priori ? le create c'est dans le form
      else if(ue.baselineOperation === EM_ACTION.CREATE) {
        // on est sur une création de ligne de scenar
        // on va jouer les triggers 
        // le field, c'est la seule value dispo
        let fieldMap: Map<string, string> = new Map();
        let targetField: string;
        [...Object.keys(ue.baseline.values || {})].forEach(field => {
          targetField = field;
          fieldMap.set(field, ue.baseline.values[field].valor);
        });

        this.fei.triggersMap?.forEach((trig: Triggers) => {
          if(trig.triggers?.has(targetField)) {

            // on a besoin de la wl (c'est pas la bonne, c'est la source sur la base de laquelle on crée la nouvelle ligne)
            let wl: WidgetLine = this.fei.getWidgetLineForKey(ue.baseline.scen, ue.baseline.key);
            const cr: ComputeResult = FormulaCoreEngine.computeMmiDependencies(trig.mm.mmi, wl, this.fei, true, fieldMap);
            
            // console.log('checkTriggers no blv', cr?.valor, trig.fkd.fullCode);
            
            if(cr?.valor?.length > 0) {       
              let blv = BaselineEngine.generateNewBaselineValue(ue.baseline, trig.fkd.fullCode);
              blv.valor = cr.valor;
              ue.baseline.values[trig.fkd.fullCode] = blv;
            }
          }
        })
      }
    });

    moreUpdates.forEach(upd => {
      // console.log('moreUpdate', upd);
      updates.push(upd);
    });
  }


  /**
   * contrôles globaux d'intégrité
   */
  public get integrityControls(): Measure[] {
    if(!this._integrityControls) {
      this._integrityControls = [];
      this.context.i?.forEach((confItem: WidgetConfigurationItemObj) => {
        let integrityFkd: FeiKeyDef = BaselineEngine.getKey(this.fei, confItem.cod);
        if (!!integrityFkd?.isMeasure) {
          this._integrityControls.push(integrityFkd.measure);
        }
      });
    }
    return this._integrityControls;
  }

  public resetIntegrityControls(): void {
    this._integrityControls = null;
  }

  /**
   * contrôles champs par champs
   */
  public get orderedControls(): Measure[] {
    if(!this._orderedControls) {
      this._orderedControls = this.model.feiEngine.builOrderedControls(this.fei);
    }
    return this._orderedControls;
  }


  public resetOrderedControls(): void {
    this._orderedControls = null;
  }

  /**
   * contrôles de cohérence des valeurs (ou de la valeur) avant persistence
   * @param updateRequest porteuse des widgetLines sources
   * @param updates porteur des demandes de modifs, pour rollback éventuel 
   * @returns true si tout est OK. Fait le rollback sur les updates dans le cas contraire
   */
  public checkBeforeCommit(updateRequest: UpdateRequest, updates: UpdateEntry[], silentMode: boolean = false): boolean {
    let hasError: boolean = false;
    // console.log('checkBeforeCommitoo', this.orderedControls, this.integrityControls);
    if (this.orderedControls?.length > 0) {
      // if (!!this.context.iupd) { // contrôle d'intégrité sur update
      let controlOK: boolean = this.integrityCheck(this.fei, this.orderedControls, updateRequest.lines, WizConstants.CHECK_MODE_CONTROL, silentMode, updates);
      if(!controlOK) {
        hasError = true;
      }     
    } 
    
    if (this.integrityControls?.length > 0) {
      // if (!!this.context.iupd) { // contrôle d'intégrité sur update
      let controlOK: boolean = this.integrityCheck(this.fei, this.integrityControls, updateRequest.lines, WizConstants.CHECK_MODE_INTEGRITY, silentMode, updates);
      if(!controlOK) {
        hasError = true;
      }     
    } 
    
    if(!!updateRequest.fkd?.isMeasure) { // simple contrôle sur la mesure en cours
      let controlOK: boolean = this.integrityCheck(this.fei, [updateRequest.fkd.measure], updateRequest.lines, WizConstants.CHECK_MODE_CONTROL, silentMode);
      if(!controlOK) {
        hasError = true;
      }
    } else { // simple contrôle sur la filterKey en cours
      if(updateRequest?.fkd?.code && updateRequest?.fkd.isFreedata) {
        if(updateRequest.value) {
          let realFDV: FreedataValueVO = this.model.freedataEngine.getFreedataValue(updateRequest.fkd.code, updateRequest.value);

          if(!realFDV) {
            hasError = true;
          }
        }
      }
      // console.log('filterKey update', updateRequest, realFDV);
    }
    
    if (hasError) { // pas bon...
      updates.forEach((update: UpdateEntry) => {
        this.model.dataEngine.rollBackUpdate(update);
      });
    } 
    return !hasError;
  }

  /**
   * altération du FEI sur retour de commit
   * update, create ou delete
   * @param e: AlkEvent, data = Map<EngineFeiVO, LoopResult> 
   */
  public recompute(e: AlkEvent): boolean {
    let loopResults: Map<FeiVO, LoopResult> = e.data[0];  
    let filterKeyUpdates: Map<Baseline, Set<string>> = e.data[1];
    let result: boolean = false;
    
    // console.log('WIDGET_ACTION_ORIGIN.RECOMPUTE', e.data);

    // il faut un FEI, et pas de recalcul pour les FREEDATAS
    if(!this.fei || this.widgetGlobalKind === WIDGET_GLOBAL_KIND.FREEDATA) {
      return;
    }

    if(loopResults.has(this.fei)) { // je suis dedans
      result = true; 
    }

    // console.log('widgetBasic recompute', loopResults);

    if(this.nextStepFei && loopResults.has(this.nextStepFei)) { // mon sous-formulaire est dedans
      // console.log('next step in', this.nextStepFei, loopResults);
      result = true; 
    }

    else if(this.fei?.scenario === WizConstants.SCEN_KEY_CODE) { // multiscen, ça me concerne
      [...loopResults.keys()].forEach((fei: FeiVO) => {
        if(fei.visionStepLevel === this.fei.visionStepLevel) {
          result = true;
        }
      });    
    } else { // en cas d'héritage multiple, je suis peut-être impacté par la modif d'un enfant (scenar)
      [...loopResults.keys()].forEach((fei: FeiVO) => {
        if(fei.visionStepLevel === this.fei.visionStepLevel && this.fei.requiredScenario.has(fei.scenario)) {
          result = true;
        }
      });   
    }

    if(result) { // je suis concerné
      // console.log('WIDGET_ACTION_ORIGIN.RECOMPUTE in', e.data);
      this.drawOrRefresh(WIDGET_ACTION_ORIGIN.RECOMPUTE, e.data);
    }

    return result;
  }

  /**
   * modification du pool de sélection sur un widget
   * je peux regarder si c'est mon maître
   * @param e: alkEvent, data = IWidget
   */
  public checkMasterSelectionUpdate(e: AlkEvent): void {
    // console.log('checkMasterSelectionUpdate', e);
    // prise de l'état pour pas jouer 2x la construction du pool
    let myState: WIDGET_STATE = this.state;

    // d'abord sur le contrôle d'état, pour terminer le cycle de vie

    // on construit le bon Event avec la bonne data
    // let ev: AlkEvent = new AlkEvent(ALK_EVENT_TYPE.FEI_COMPUTED, e.data?.fei);
    this.checkFeiState(e);

    // ne pas passer 2x pour les widgets...
    if(myState >= WIDGET_STATE.READY) { // seulement si je suis prêt
      // console.log('checkMasterSelection', e.data);

      let masters: Map<string, IWidget> = this.model.widgetEngine.checkMasters(this);
      // console.log('master', this, master, e.data);
      if(masters?.has(e.data?.uid)) {
        // console.log('update select on master', this.widgetKind, this.fei.uniqueName, master, e.data);
        this.alterContent(WIDGET_ACTION_ORIGIN.OUTER_SELECT);
      }

    }
  }

  
  /* ##############################################################################
          gestion du contexte de dates pour les widgets de type calendrier
  ############################################################################## */

  /**
   * détection du changement de date ou du mode de vue
   * pour sauvegarde du contexte
   * @param arg 
   */
  public changeDate(arg: any): void {
    // console.log('changeDate', arg);
    this.context.cp = [];
    this.context.cp.push({cod: 'mode', val: arg.view.type});
    this.context.cp.push({cod: 'day', val: WizDateUtils.dateToString(arg.start, 'yyyy-MM-dd')});
    if (!this.templatingMode) {
      // persistence uniquement hors du template, sinon, c'est délégué
      this._saveContext();
    } else {
      this.settings = this.context;
      WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.WIDGET_SET_SETTINGS, this));
    }
  }

  /**
   * prise de la date sur le contexte
   * @returns 
   */
  public getContextDate(): string {
    let result: string = WizDateUtils.dateToString(WizDateUtils.TODAY, 'yyyy-MM-dd') ;
    if(!!this.context.cp) {
      this.context.cp.forEach(wkp => {
        if(wkp.cod === 'day') {
          result = wkp.val.toString();
        }
      });
      
    }
    return result;
  }

  /**
   * prise du mode d'affichage sur les timelines
   * @returns 
   */
  public getContextMode(): string {
    let result: string = 'meYear';
    if(!!this.context.cp) {
      this.context.cp.forEach(wkp => {
        if(wkp.cod === 'mode') {
          result = wkp.val.toString();
        }
      });
    }
    return result;
  }

   /* ##############################################################################
          tools
  ############################################################################## */
 

  /** demande d'export depuis le bouton */
  public exportDatas(): void {
  }

  /**
   * mise à jour de la valeur d'une cellule en édition directe
   * Donc 1 seule ligne, sinon il faut passer par la pop
   *
   * @param cell contient le node (la ligne), la WidgetLine porteuse, la colDef, la oldValue et la newValue
   */
  public updateCell(cell: any): boolean {
    // pour override
    return true;
  }


  /**
   * drop d'un fichier excel (excelExportEngine)
   * @param sheet 
   */
  public fileDrop(sheet: ExcelSheetVo): void {
    // pour override
  }


   /* ##############################################################################
              contrôle en édition et saisie
  ############################################################################## */
 

  /** fonction de restriction d'édition, directement depuis la cellule */
  public checkEditRestrictFromCell(params: any, silentMode: boolean = false) : boolean {
    const fkd: FeiKeyDef = params.colDef.fkd;
    
    const meas: Measure = fkd.measure;
    if(!meas || !meas.editRestrict) {
      return true;
    }
    
    const wizColDef: WizColDef = params.column.colDef as any as WizColDef;
    const rowLine: WidgetLine = params.data;
    const pivot: Pivot = wizColDef.widget as any as Pivot; // TODO faire marcher avec form & calendar Etendre la ColDef pour gérer proprement le wizPivot
    const linePack: WidgetLine[] = pivot.pivotMode ? pivot.getPool(rowLine, params.column) : [rowLine];
    const res = this.checkEditAvailable(this.fei, meas, linePack, silentMode);
    
    return res;
  }

  /** 
   * fonction de restriction d'édition
   * 
   * @param fei cible
   * @param meas mesure cible
   * @param wls pack de lignes
   * @param silentMode dispatch de l'erreur si false
   * @returns "Editable = TRUE" si la fonction editRestrict de la mesure retourne TRUE
   */
   public checkEditAvailable(fei: FeiVO, meas: Measure, wls: WidgetLine[], silentMode: boolean = false) : boolean { 
    
    if (!meas || !meas.editRestrict) {
      return true;
    }
    
    const allowed : boolean = this.model.widgetEngine.computeEditRestrict(fei, meas, wls);
    if(!allowed && !silentMode) {
      // console.log('wb checkEdit', meas)
      let cleanMess: string = this.model.measureEngine.getMeasureLabel(meas);
      cleanMess = cleanMess  + '<br>';
      cleanMess +=  this.model.measureEngine.getMeasureEditRestrictMessage(meas);
      WizPopUp.toast(cleanMess, WizIcon.TOAST_ERROR).show();
    }
    return allowed;
  }

  /** 
   * contrôle d'intégrité avant update / suppression
   * @param meas pack de mesures du contrôle d'intégrité
   * @param pls pack de lignes concernées
   * @param fakeMap: Map<string, string> les valeurs en cours de modif
   * @param mode EDIT_RESTRICT ou VALUE_CONTROL
   * @param silentMode silencieux: pas de toast
   * @returns true si intégrité (toutes les fonctions de contrôle sont OK)
   */
  public integrityCheck(fei: FeiVO, meas: Measure[], pls: WidgetLine[], mode: string = WizConstants.CHECK_MODE_RESTRICT, 
    silentMode: boolean = false,
    updates: UpdateEntry[] = null) : boolean { 

    // TODO TODO ici, en cas d'update sur les Filters, faire des fakes sur les lines ?

    const errorMap: Map<Measure, string> = this.model.widgetEngine.computeIntegrity(fei, meas, pls, mode, updates);
    if(!!errorMap && errorMap.size > 0) {    
      if (!silentMode) {

        // TODO ici un seul joli toast
        // faire un set sur les libellés, possiblement le même...
        const maxDisplayErrors =  [...errorMap.keys()].slice(0,2);
        
        // console.log('wb integrityCheck', maxDisplayErrors);
        maxDisplayErrors.forEach(meas => {
          let cleanMess: string = this.model.measureEngine.getMeasureLabel(meas);
          cleanMess = cleanMess  + '<br>';
          cleanMess +=  errorMap.get(meas);
          WizPopUp.toast(cleanMess, WizIcon.TOAST_ERROR).show();
        });
          
      }
      return false;
    }
    return true;
  }

  /**
   * demande de suppression en masse pour les widgets de type Freedata
   * pour override dans FreedataWidgetBasic
   * @param e 
   */
  public massDeleteRequest(e: AlkEvent): void {

  }

  /**
   * modif des droits. Pour override dans le grid pour gestion du rendu des headers
   * @param context 
   */
  public rightsUpdate(context: WidgetConfigurationObj): void {
    // console.log('rightsUpdate', context);
  }


  public backFromEditPop(data: PivotCellEditPopData): void {
  }
 
  /**
   * consolidation du resultat en mode revert
   * pour override dans pivot
   * @param params 
   * @returns 
   */
  public revertValueAccessor(params: any): any {
    let result: string = ''; // retour, pas de null
    return result;
  }

  /**
   * formatage du resultat en mode revert
   * pour override dans pivot
   * @param params 
   * @returns 
   */
  public revertFormatterValueAccessor(params: any): any {
    let result: string = ''; // retour, pas de null
    return result;
  }

  /**
   * méthode d'accès générique sur les baselineValues.valor, depuis la WidgetLine
   * @param params la conf d'une cellule de grid. .data contient une WidgetLine
   */
  public valueAccessor(params: any): any {
    
    let result: string = ''; // retour, pas de null

    if(this._context?.revert) { // mode revert, on délègue
      return this.revertValueAccessor(params);
    }

    const line: WidgetLine = params.data; // ligne source
    const colDef: WizColDef = params.colDef; // définition de la colonne
    //const code: string = params.colDef.field; // champ mesure cible code#mode|fonction d'aggrégat
    const fkd: FeiKeyDef = params.colDef.fkd; // PKD

    if(line.isTotalLine) { // ligne de cumul général
      // le pool de lignes
      let childPool: WidgetLine[] = this.getPool(line, params.column);
      let aggMethod: string = this.model.dataEngine.pkdDefaultAggregateFunction(colDef.datakind);

      if(params.colDef.wcio.totAgg) {
        aggMethod = params.colDef.wcio.totAgg;
      }
     
      if(colDef.place === 'row') {
        // pas de cumul pour les rows
        // console.log('row total', params, result);
      } else {
        if(aggMethod === AggregateEngine.AGG_COMPUTE) { // calcul sur les données aggrégées, par sur la source
          childPool = this.mainDS.getSource(); // TEST LMA
          result = this.model.aggregateEngine.PIVOTME(params, this, childPool); 
          // console.log('AGG_COMPUTE', code, result);
        }
        else {
          result = this.model.aggregateEngine.call(aggMethod, this.fei, childPool, fkd); 
          // console.log('simple', code, result);
        }
      }


       
      // if(aggMethod === 'COUNTTRUE') {
      //   console.log('total valueAccessor', line.key, params.colDef.fkd.fullCode, aggMethod, result);
      // }

      // console.log('isTotalLine', colDef, childPool?.length, result);
      // result = '666';
    } else if(line.isSubTotalLine) { // ligne de pivot consolidée
        
        if(params.colDef.place === 'row') { // utilisation d'une mesure en ligne pour un pivot   
          // prise du 1er children
          let theOne: WidgetLine = line.ch?.size > 0 ? [...line.ch.values()][0] : null ;
          
          // if(params.colDef.field === "FRN-M_CROSS-KEY#I") {
          //   console.log('FRN-M_CROSS-KEY#I', fkd.fullCode, theOne);
          // }
          // creation de la cellule si inexistante
          if(!theOne?.cells?.get(fkd.fullCode)) {
            theOne.cells.set(fkd.fullCode, { k: theOne.key, m: fkd.fullCode, l: theOne.level, sc: theOne.bl.scen });
          }

          result = BaselineEngine.getValue(this.fei, theOne, theOne?.cells?.get(fkd.fullCode)); 


        } else {
          // le pool de lignes
          let childPool: WidgetLine[] = this.getPool(line, params.column);
          let aggMethod: string = this.model.dataEngine.pkdDefaultAggregateFunction(fkd.datakind);
          if(this.context?.pmd) {
            if(params.colDef.wcio.totAgg) {
              aggMethod = params.colDef.wcio.totAgg;
            }
          } else{
            if(params.colDef.wcio.agg) {
              aggMethod = params.colDef.wcio.agg;
            }
          }

          if(aggMethod === AggregateEngine.AGG_COMPUTE) { // calcul sur les données aggrégées, par sur la source
            result = this.model.aggregateEngine.PIVOTME(params, this, childPool); 
          }
          else {
            result = this.model.aggregateEngine.call(aggMethod, this.fei, childPool, fkd); 
          }
        }

    } else { // ligne de base (soit Grid, soit détail du pivot)
      
      // en cas de pivot, la ligne doit être éligible (si utilisation de colonnes)
      let imOK: boolean = true;
      if(this.pivotMode) {
        let childPoolSingle: WidgetLine[] = this.getPool(line, params.column, true);
        if(!(childPoolSingle.length > 0)) {
          imOK = false;
        }
      }
      if(imOK) {
        // si la cellule n'existe pas encore sur la baseline, création
        // if(fkd.fullCode === 'LM_Q#I') {
        //   console.log('no cell ?', line.cells);
        // }

        if(!line.cells?.get(fkd.fullCode)) {
          line.cells.set(fkd.fullCode, { k: line.key, m: fkd.fullCode, l: line.level, sc: line.bl.scen });
        }

        result = BaselineEngine.getValue(this.fei, line, line.cells.get(fkd.fullCode)); 

        // if(line.key === '152' && fkd.fullCode === 'M_COLLM#I') {
        //   console.log('M_COLLM#I', result);
        // } 
      }

      // neutralisation du undefined sur les checkboxes : passage à false en mode simple
      if ((!line.isSubTotalLine && !line.isTotalLine) && colDef.datakind === FIELD_KIND.BOOLEAN) {
        if (!result || result === WIZ_BOOLEAN.UNKNOWN) {
          result = WIZ_BOOLEAN.FALSE;
          // console.log('value neutr', line.key, line.isTotalLine, line.isSubTotalLine, result);
        }
      }

    }

    // console.log('value', line.key, line.isTotalLine, line.isSubTotalLine,params.colDef, result);
    return result;
  }

  /** Formatte la valeur (mesure) pour un affichage cohérent */
  public valueFormatterAccessor(params: any): string {
    
    // if(params.value === "153@M_DEV-COM-DEV#I") {
    //   console.log('got you', params);
    // }

    if(this._context?.revert) { // mode revert, on délègue
      return this.revertFormatterValueAccessor(params);
    }
    
    const line: WidgetLine = params.data; // ligne source
    const colDef: WizColDef = params.colDef; // définition de la colonne
    const fkd: FeiKeyDef = params.colDef.fkd;

    let result: string = null;



    // interception LIST 
    if(fkd.datakind === FIELD_KIND.LIST) { 
      return this.patchListFormatter(params);
    } 
    // interception THRESHOLD 
    else if(fkd.datakind === FIELD_KIND.THRESHOLD) { 
      return this.patchThresholdFormatter(params);
    } 
    // interception FILTER 
    else if(fkd.datakind === FIELD_KIND.FILTER) { 
      return this.patchFilterFormatter(params);
    } 
    // interception CROSS_FILTER 
    else if(fkd.datakind === FIELD_KIND.FILTER) { 
      return this.patchCrossFilterFormatter(params);
    } 
    // interception Workflow 
    else if(fkd.datakind === FIELD_KIND.WORKFLOW) { 
      // console.log('datakind WORKFLOW', params.colDef?.wcio?.fdvf);
      return this.model.workflowEngine.valueFormatter(params);
    }      


    let aggMethod: string = null;
    let format: string = colDef.dataformat || fkd.defaultFormat;

    if(line.isTotalLine) { // ligne de cumul général
      aggMethod = this.model.dataEngine.pkdDefaultAggregateFunction(colDef.datakind);
      
      if(colDef.wcio?.totAgg) {
        aggMethod = colDef.wcio.totAgg;
      }
      if(colDef.wcio?.totFmt) {
        format = colDef.wcio.totFmt;
      }

      // if(aggMethod === 'COUNTTRUE') {
      //   console.log('total valueFormatterAccessor', line.key, params.colDef.fkd.fullCode, aggMethod, format, result);
      // }

    } else if(line.isSubTotalLine) { // ligne de pivot consolidée
      aggMethod = this.model.dataEngine.pkdDefaultAggregateFunction(colDef.datakind);
      if(this.context?.pmd) {
        if(colDef.wcio?.totAgg) {
          aggMethod = colDef.wcio.totAgg;
        }
        if(colDef.wcio?.totFmt) {
          format = colDef.wcio.totFmt;
        }
      } else{
        if(colDef.wcio?.agg) {
          aggMethod = colDef.wcio?.agg;
        }
      }
    } else { // ligne de base (soit Grid, soit détail du pivot)     
      aggMethod = null;
    }
    
    // prise du format sur le FKD, le colDef.datakind est possiblement un aggrégat d'un autre type
    let datakind: string = fkd.datakind;
    if(aggMethod) { // surcharge éventuelle du format de rendu par l'aggrégat
      let aggMethodDatakind: string = this.model.aggregateEngine.functionMap().get(aggMethod).dataKind;
      if(!!aggMethodDatakind) {
        datakind = aggMethodDatakind;
      };
    }

    // LMA TODO contrôler : patch mesures en keys de pivot
    if((line.isTotalLine || line.isSubTotalLine) && colDef.place === 'row') {
      // console.log('valueFormatterAccessor', params.value, colDef);
      if(params.value) {
        return params.value;
      } else {
        return '';
      }
      
    }
    
    result = this.model.formatEngine.applyFormat(params.value, datakind, format, aggMethod);   
    // console.log('valueFormatter', line.key, line.isTotalLine, line.isSubTotalLine,params.colDef, params.value,  result);
    return result;
    
  }

  // patch pour le rendu des lists
  public patchListFormatter(params: any): string {
    const fkd: FeiKeyDef = params.colDef.fkd;
    let result: string;

    const line: WidgetLine = params.data; // ligne source
    const colDef: WizColDef = params.colDef; // définition de la colonne
    const code: string = params.colDef.field; // champ mesure cible code#mode|fonction d'aggrégat
    // identifiant unique de la colonne
    const colId: number = params.column.instanceId;
    // valeur calculée par keyValueAccessor
    const val: string = params.value;
    // le format
    const format: string = params.colDef.dataformat;

    // patch gestion de l'extraction en mode brute
    if(format) {
      try {
        let formatKind: string = format.split(WizConstants.KEY_SEPARATOR)[1];
        if(formatKind === WizConstants.RAW) {
          result = params.value;
          // console.log('list format', format, result);
          return result;
        }
      } catch(e: any) {

      }    
    }


    if (this.widgetLineCellRendererExist(line, colId, val, format) ) { // déjà mémorisé
      result = this.getWidgetLineCellRendererValue(line, colId, val, format);
      return result;
    } 
    
    let childPool: WidgetLine[];
    if (!colDef.widget.pivotMode) { // mode simple (GRID)
      childPool = [line];
    } else {
      childPool = this.getPool(line, params.column);
    }

    result = this.model.listFieldEngine.generateRenderer(this.fei, fkd, childPool);
    // on mémorise
    this.setWidgetLineCellRendererValue(line, colId, val, format, result);
    return result;
  }

  // patch pour le rendu des mesures de type FILTER 
  public patchFilterFormatter(params: any): string {
    const fkd: FeiKeyDef = params.colDef.fkd;
    let result: string;

    const line: WidgetLine = params.data; // ligne source
    const colDef: WizColDef = params.colDef; // définition de la colonne
    const code: string = params.colDef.field; // champ mesure cible code#mode|fonction d'aggrégat
    // identifiant unique de la colonne
    const colId: number = params.column.instanceId;
    // valeur calculée par keyValueAccessor
    const val: string = params.value;
    // le format
    const format: string = params.colDef.dataformat;

    if(params.value === null || params.value === undefined) {
      return null;
    }

    // TODO prise du FD sur le settings
    // prise du FDV
    // puis collecte du champ label ou autre depuis settings
    // définitiond du type final ?


    if (this.widgetLineCellRendererExist(line, colId, val, format) ) { // déjà mémorisé
      result = this.getWidgetLineCellRendererValue(line, colId, val, format);
     
        // if(line.key === '152' && fkd.fullCode === 'M_COLLM#I') {
        //   console.log(' widgetLineCellRendererExist M_COLLM#I', val, result);
        // } 
      
      // console.log('patchFilterFormatter intercept', params.value, result);
      return result;
    } 

    try {
      let FDCode: string = (fkd.measure.settings as any).keys[0].FD;
      let FDVCode: string = val;
      if(FDCode && FDVCode) {
        let renderField: string = (fkd.measure.settings as any).keys[0].render || ViewData.FIELD_LABEL;
        let fdv: FreedataValueVO = this.model.freedataEngine.getFreedataValue(FDCode, FDVCode);
        if(!!fdv) {
          result = this.model.freedataEngine.getFDValue(fdv, renderField);
        }

        // if(line.key === '152' && fkd.fullCode === 'M_COLLM#I') {
        //   console.log(' not RendererExist M_COLLM#I', val, result);
        // } 

      }
    } catch (e:any) {
      // console.log('patchFilterFormatter', params.value);
    }

    
    // TODO TODO gestion pivot
    // let childPool: WidgetLine[];
    // if (!colDef.widget.pivotMode) { // mode simple (GRID)
    //   childPool = [line];
    // } else {
    //   childPool = this.getPool(line, params.column);
    // }

    // TODO

    // console.log('patchFilterFormatter', params.value, params.colDef.fkd, result);
    // on mémorise
    // console.log('patchFilterFormatter generate', params.value, result);
    this.setWidgetLineCellRendererValue(line, colId, val, format, result);
    return result;
  }

  // patch pour le rendu des seuils
  public patchThresholdFormatter(params: any): string {
    const fkd: FeiKeyDef = params.colDef.fkd;
    let result: string;

    const line: WidgetLine = params.data; // ligne source
    const colDef: WizColDef = params.colDef; // définition de la colonne
    const code: string = params.colDef.field; // champ mesure cible code#mode|fonction d'aggrégat
    // identifiant unique de la colonne
    const colId: number = params.column.instanceId;
    // valeur calculée par keyValueAccessor
    const val: string = params.value;
    // le format
    const format: string = params.colDef.dataformat;

    // patch gestion de l'extraction en mode brute
    if(format) {
      try {
        let formatKind: string = format.split(WizConstants.KEY_SEPARATOR)[1];
        if(formatKind === WizConstants.RAW) {
          result = params.value;
          // console.log('list format', format, result);
          return result;
        }
      } catch(e: any) {

      }    
    }


    if (this.widgetLineCellRendererExist(line, colId, val, format) ) { // déjà mémorisé
      result = this.getWidgetLineCellRendererValue(line, colId, val, format);
      return result;
    } 
    
    let childPool: WidgetLine[];
    if (!colDef.widget.pivotMode) { // mode simple (GRID)
      childPool = [line];
    } else {
      childPool = this.getPool(line, params.column);
    }

    result = this.model.listFieldEngine.generateThresholdRenderer(this.fei, fkd, childPool);
    // on mémorise
    this.setWidgetLineCellRendererValue(line, colId, val, format, result);
    return result;
  }  

  /**
   * méthode d'accès aux Keys / filterKeys du pivot
   * retourne la valeur de la cellule (pour les sorts, par exemple)
   * @param params la conf d'une cellule de grid. .data contient une WidgetLine
   */
  public keyValueAccessor(params: any): any {
    
    // le résultat. Pas de null ici, pour pouvoir comparer les chaines vides
    let result: string = null;

    const line: WidgetLine = params.data; // ligne source
    if(!line) { // pas de ligne, filtre user
      return result;
    }
    
    const colDef: WizColDef = params.colDef; // définition de la colonne
    
    const fkd: FeiKeyDef = params.colDef.fkd; // FKD
    const fdvfield: string = params.colDef.wcio.fdvf;
    
    if(line.isTotalLine && params.colDef.place === 'row') { // ligne de cumul uniquement pour les datas 
      result = null;
      // console.log('total keyValueAccessor', line.key, params.colDef.fkd.fullCode, result);
    } else if((line.isTotalLine || line.isSubTotalLine) && params.colDef.place === 'data') { // ligne de cumul uniquement pour les datas 
      let aggResult: string[] = []; // array, car cumul possible en cas de Pivot     
      let childPool: WidgetLine[] = this.getPool(line, params.column);
      let aggMethod: string = this.model.dataEngine.pkdDefaultAggregateFunction(colDef.datakind);

      if(this.context?.pmd) { // mode gigogne
        if(params.colDef.wcio.totAgg) {
          aggMethod = params.colDef.wcio.totAgg;
        }
      } else {
        if(line.isTotalLine) {
          if(params.colDef.wcio.totAgg) {
            aggMethod = params.colDef.wcio.totAgg;
          }
        } else {
          if(params.colDef.wcio.agg) {
            aggMethod = params.colDef.wcio.agg;
          }
        }
      }
 
      if (fkd.isChrono) { // chrono en data, résolution de la méthode d'aggrégat
        let chronoField: string = fdvfield || WizConstants.FIELD_STARTDATE; // si aucun champ spécifié, on prend la date de début
        // TODO TODO retourner un array ??
        aggResult = [this.model.aggregateEngine.call(aggMethod, this.fei, childPool, fkd, chronoField)];
        // console.log('isTotalLine keyValueAccessor', line, chronoField, aggResult);
      } else { // un FDV
        aggResult = [this.model.aggregateEngine.call(aggMethod, this.fei, childPool, fkd, fdvfield)]
      } 
      result = aggResult.join(ViewData.KEY_SEPARATOR);
    
      // console.log('total keyValueAccessor', line.key, params.colDef.fkd.fullCode, aggMethod, result);


    } else if(line.isSubTotalLine && params.colDef.place === 'row') { // ligne de pivot en row, valeur unique
      
      if(line.subTotalLevel > 0 && !line.subTotalKeySet.has(fkd.fullCode)) { // uniquement les éléments désirés pour la colonne
        return null;
      } 

      if (fdvfield && fkd.isFreedata) { // je recherche un sous-champ sur un FDV (donc le code d'un autre FDV)
        result = BaselineEngine.getLabel(this.fei, line, fkd.fullCode, fdvfield || WizConstants.FIELD_LABEL); // va retourne le libellé lié à la propriété fdvfield
      } else { // sinon on travaille avec la sortKey (pour tri). Elle est remplacée par la Key dans le keyFormatterAccessor
        result = BaselineEngine.getSort(this.fei, line, fkd.fullCode, WizConstants.FIELD_LABEL); 
      }
            

    } else { // ligne de base (soit Grid, soit détail du pivot)
      let exist: boolean = true;
      if(this.context.pmd && this.context.c?.length > 0 && params.colDef.place === 'data') { // on utilise les cumuls, et des colonnes, vérifier que j'existe
        let childPool: WidgetLine[] = this.getPool(line, params.column, true);
        // console.log('line', line.key, params.column.colId, childPool);
        if(!(childPool?.length > 0)) {
          exist = false;
        }
      }
      
      if(exist) {
        if (fdvfield && fkd.isFreedata) { // je recherche un sous-champ sur un FDV (donc le code d'un autre FDV)
          result = BaselineEngine.getLabel(this.fei, line, fkd.fullCode, fdvfield || WizConstants.FIELD_LABEL); // va retourne le libellé lié à la propriété fdvfield
          // result = BaselineEngine.getSort(this.fei, line, fkd.fullCode, WizConstants.FIELD_LABEL); // va retourne le libellé lié à la propriété fdvfield
        } else { // sinon on travaille avec la sortKey (pour tri). Elle est remplacée par la Key dans le keyFormatterAccessor
          // if(params.colDef.headerName === 'Modèle') {
          //   console.log('bad model');
          // }
          result = BaselineEngine.getSort(this.fei, line, fkd.fullCode, WizConstants.FIELD_LABEL); 
        }
      } 
      // console.log('keyValueAccessor',params.colDef.headerName, fdvfield, fkd.fullCode, result);
    }

    // if (!result || result === undefined) {
    //   result = ''; // neutralisation des null / undefined pour comparaison avec ''
    // }

    if ( result === undefined) {
      result = null; // neutralisation des null / undefined pour comparaison avec ''
    }
    // console.log('keyValueAccessor final',params.colDef.label, fkd.fullCode, result);
    return result;
  }

  /**
   * méthode d'accès aux Keys / filterKeys du pivot
   * retourne la valeur formatée (label)
   * @param params la conf d'une cellule de grid. .data contient une WidgetLine
   */
  public keyFormatterAccessor(params: any): any {

    let result: any = params.value || '';
  
    const line: WidgetLine = params.data; // la widgetLine
    if(!line || line === undefined) {
      // console.log('keyValueAccessor no line', params);
      return null;
    }

    // le fkd
    let fkd: FeiKeyDef = params.colDef.fkd;
    // le code du champ. (CodeFD#level)
    let code: string = fkd.fullCode;
    // le champ à utiliser sur le FDV
    const fdvfield: string = params.colDef.wcio.fdvf || WizConstants.FIELD_LABEL;
    // identifiant unique de la colonne
    const colId: number = params.column.instanceId;
    

    // prise du format
    let format: string = params.colDef.dataformat;

    // prise de la méthode d'aggrégat
    let aggMethod: string = this.model.dataEngine.pkdDefaultAggregateFunction(params.colDef.datakind);
    
    // prise du datakind réel
    let datakind: string = params.colDef.datakind

    if(line.isTotalLine) { // ligne de cumul général
      let aggMethod: string = this.model.dataEngine.pkdDefaultAggregateFunction(params.colDef.datakind);
      if(params.colDef.wcio?.totAgg) {
        aggMethod = params.colDef.wcio.totAgg;
        let aggMethodDatakind: string = this.model.aggregateEngine.functionMap().get(aggMethod).dataKind;
        if(!!aggMethodDatakind) {
          datakind = aggMethodDatakind;
        }
      }
    
      if(params.colDef.wcio?.totFmt) {
        format = params.colDef.wcio.totFmt;
      }     
    } else if(line.isSubTotalLine && this.context?.pmd && params.colDef.place === 'data') { // ligne de pivot consolidée
      if(this.context?.pmd) {
        if(params.colDef.wcio?.totAgg) {
          aggMethod = params.colDef.wcio.totAgg;
          let aggMethodDatakind: string = this.model.aggregateEngine.functionMap().get(aggMethod).dataKind;
          if(!!aggMethodDatakind) {
            datakind = aggMethodDatakind;
          }
        }

        if(params.colDef.wcio?.totFmt) {
          format = params.colDef.wcio.totFmt;
        }
      }
    }

    // déjà mémorisée, retour rapide
    if (this.widgetLineCellRendererExist(line, colId, result, format) ) { 
      result = this.getWidgetLineCellRendererValue(line, colId, result, format);
      //console.log('(this.widgetLineCellRendererExist', result);
      return result;
    } 


    // masquer les rows sur les lignes de détail en mode pmdEx
    if(this.context.pmdex && params.colDef.place === 'row' && !(line.isTotalLine || line.isSubTotalLine)) {
      return '';
    }
    if(line.isTotalLine && params.colDef.place === 'row') { // ligne de cumul général
      // console.log('isTotalLine on row', line, code, result); 
      result = '';   
    } else if(line.isTotalLine || line.isSubTotalLine && params.colDef.place === 'data') { // ligne de pivot consolidée
      // if(line.isTotalLine) {
      //   // console.log('isTotalLine on data', line, code, result);
      // }
      if (result?.length > 0) {
        
        // if(fkd.datakind === FIELD_KIND.WORKFLOW) {
        //   result = this.model.workflowEngine.valueFormatter(params);
        //   console.log('subWF', result, params.value);
        // } else {
          result = this.model.formatEngine.applyFormat(result, datakind, format, null);
        // }
        
       
        // if (fkd.isChrono) { // gestion du rendu des chronos. ? déjà fait dans
        //     //console.log('isTotalLine on chrono', line, code, result);
        //     // if (!!format) {
        //       result = this.model.formatEngine.applyFormat(result, datakind, format, null);
        //     // } else {
        //     //   result = BaselineEngine.getLabel(this.fei, line, code, fdvfield); 
        //     // }       
        // } else { // les FDV ? TODO TODO passer des array au formatter...pas de format, on assemble les valeurs avec ,
        //   result = params.value.split(ViewData.KEY_SEPARATOR).join(',');
        //   result = this.model.formatEngine.applyFormat(result, datakind, format, null); // et formater
        // }  
      }
    } else { // ligne de base (soit Grid, soit détail du pivot) ou consolidation en ROW
      

      // let finalFKD: FeiKeyDef = fkd;
      // console.log('simple baseLine', line, code, result);
      if (!!result) {

        // interception labels colonne unique
        let uniqueColumn: boolean = this.context.pmdex && !!this.model.widgetEngine.pmdExParams(this.context)?.uniqueColumn;
        if(line.isSubTotalLine && uniqueColumn) {
          // console.log('you', line, line.subTotalLevel, this.context.r[line.subTotalLevel-1]);

          if(uniqueColumn) {
            fkd = this.fei.keyMap.get(this.context.r[line.subTotalLevel-1].cod);
            code = fkd.fullCode;
          }
        }

        const fdvfield: string = params.colDef.wcio.fdvf; 
        if(!!fdvfield) { // formatage complémentaire pour les rendus FDVs avec propriété (champ custom)
          if(params.colDef?.datakind === FIELD_KIND.WORKFLOW) {
            result = this.model.workflowEngine.valueFormatter(params);
            // console.log('subWF', result, params.value);
          } else {
            
            result = BaselineEngine.getLabel(this.fei, line, code, fdvfield); // prendre la bonne valeur de champ FD
          
            // console.log('pivot format', fdvfield, result);
            // possible override du type de rendu selon le champ sur un chrono
            if(fkd.isChrono) {
              datakind = this.model.chronocodeEngine.getChronoFieldByCode(fdvfield).datakind;
            } 
            result = this.model.formatEngine.applyFormat(result, datakind, format, null); // et formater
          }
        } else { // pas de champ sur le FDV    
          if(!!format) { // un format custom sur la valeur raw
            result = this.model.formatEngine.applyFormat(result, datakind, format, null); // et formater
            // console.log('keyFormatter format', result);
          } else { // pas de format, on prend le label brut de pomme
            result = BaselineEngine.getLabel(this.fei, line, code, WizConstants.FIELD_LABEL); 
          }            
        }

      }
    }

    // on mémorise
    this.setWidgetLineCellRendererValue(line, colId, params.value || '', format, result);

    return result;
  }

  /**
   * gestion custom des tri pour rowGroups
   * @param e 
   */
  protected customSort(e: AlkEvent): void {
    // console.log('customSort', e);
    this.forcePMDSort(e.data);
  }

  /**
   * gestion du tri
   * Intercepte les demandes des headers pour éventuellement forcer l'ordre des rows pmd
   */
  protected forcePMDSort(newSort: any = null): void {
    
    if(newSort && !this.context.pmd) { // demande de tri simple
      newSort.params.setSort(newSort.sort, newSort.shift);
      const colParams = this['agGrid']?.columnApi.getColumnState();
      if(!!colParams) {
        const cleans: any[] = []; // on ne prend que le sort (et on enlève les champs inutiles)
        colParams.forEach((param: any) => {
          let sortObj: any = { colId: param.colId, sortIndex: 0, sort: param.sort };   
          if(!!param.sort) {// } && !!param.sortIndex) {
            sortObj.sortIndex = param.sortIndex;
            cleans.push(sortObj);
          } 
          
        });

        let hasUpdate: boolean = !isEqual(this.context.sort, cleans);
        if(hasUpdate) { //maj si modifs uniquement
          this.context.sort = cleans;
          this.updateContext(this.context, true); 

          // ici détection du sort pour le mode compute
          this.drawOrRefresh(WIDGET_ACTION_ORIGIN.SORT, this.context.sort);

        }
      }
      
      return;
    }

    // par défaut, prise du contexte
    let result: WidgetSort[] = clone(this.context.sort) || [];

    // let state: any = this.context.sort;
    if(this.pivotMode && this.context.pmd) { // mode pivot et détail, les lignes rows doivent être dans le sort
      
      const context: WidgetConfigurationObj = this._context;
      // console.log('forcePMDSort',colParams);
      // let realState: any[] = [];
      let realStateMap: Map<string, WidgetSort> = new Map();
      let idx: number = 0;

      // prise des lignes pour forcer
      this.context.r.forEach((wcio: WidgetConfigurationItemObj) => {
        // console.log('r', wcio);
        let sortObj: WidgetSort = { colId: wcio.cod, sortIndex: idx, sort: 'asc' };  
        realStateMap.set(sortObj.colId, sortObj);
        idx++; 
      });
      
      // ajout de la colonne de cumul
      let sortObj: WidgetSort = { colId: 'GRP', sortIndex: idx, sort: 'desc' };  
      realStateMap.set(sortObj.colId, sortObj);
      idx++; 

      let existSort: WidgetSort[] = [...result]; // shift, reprendre les colonnes existantes
      if(newSort) {
        if(!newSort.shift) {
          existSort = [];
        }
        existSort.push(newSort);
      }    
     
      // demande de sort en cours 
      existSort.forEach((ws: WidgetSort) => {  
        if(realStateMap.has(ws.colId)) { // modif sur les lignes figées
          if(!ws.sort) {
            ws.sort = 'desc';
          }
          realStateMap.get(ws.colId).sort = ws.sort;
        } else {
          let sortObj: any = { colId: ws.colId, sortIndex: idx, sort: ws.sort };
          idx++;
          realStateMap.set(sortObj.colId, sortObj);
        }
      });

      result = [...realStateMap.values()].sort((a, b) => a.sortIndex - b.sortIndex);
    } 

    let hasUpdate: boolean = !isEqual(this.context.sort, result) ;

    if(hasUpdate) { //maj si modifs uniquement
      this.context.sort = result;

      this['agGrid'].columnApi.applyColumnState({ state: this.context.sort, defaultState: { sort: null } });
      this.updateContext(this.context, true); 

      // this.updateContext(this.context, true); 
    } else if(!newSort) { // appel initial
        this['agGrid'].columnApi.applyColumnState({ state: this.context.sort, defaultState: { sort: null } });
    }
  }

  /**
   * on dessine pas la pagination si on est pas en mdoe compute
   * @param e 
   */
  private computeDrawPages(e: AlkEvent = null): void {
    if(this.model?.widgetEngine?.widgetIsCompute(this)) {
      this.drawPages(e);
    }
  }

  /** pour override, affichage de la pagination (sur retour de chargement de page) */
  public drawPages(e: AlkEvent = null): void {}

  /**
   * sur socketEvent compute, mise à jour du grid
   * @param e 
   */
  public reloadCompute(e: AlkEvent): void {
    // console.log('reloadCompute', e);
    if(this.fei?.computeMode === COMPUTE_MODE.COMPUTE) {
      this.computeRebuild(e);

      let current: number = this.context.cmp || 1;
      let pageEvent: PageEvent = {kind: PAGE_EVENT_KIND.ABS, vector: current};
      this.computeGet(new AlkEvent(ALK_EVENT_TYPE.COMPUTE_UPDATE, pageEvent));
    }
  }

  /**
   * modif structurelle => reconstruction structure compute
   */
  public computeRebuild(e: AlkEvent):void {
    // TODO TODO seulement si je suis concerné ??
    try {
      // console.log('computeRebuild', this.uid);
      this.fei.pages.delete(this.uid);
      this.fei.pageSize.delete(this.uid);
      this.fei.totalSizes.delete(this.uid);
    } catch (e: any) {

    }
  }

  /**
   * pour override dans pivot
   * @param e 
   */
  public computeFastSync(e: AlkEvent): void {
    // TODO TODO seulement si je suis concerné ??
  }

  /**
    * demande chargement d'une page spécifique
    */
   public computeGet(e: AlkEvent):void {
     // TODO TODO seulement si je suis concerné ??
     try {

      let pageE: PageEvent = e.data;
      let realPage: number = 1;
      if(pageE.kind === PAGE_EVENT_KIND.ABS) {
        realPage = pageE.vector;
      } else {
        realPage = this.context.cmp + pageE.vector;
      }

      // on pose le paramètre sur le FEI pour ce Widget
      if(!this.fei.requestPage) {
        this.fei.requestPage = new Map();
      }
      this.fei.requestPage.set(this.uid, realPage);

      // console.log('widget basic computeGet: ', this, realPage);
      this.drawOrRefresh(WIDGET_ACTION_ORIGIN.GET_PAGE); // va déclencher un buildLine, et donc un appel remote
     } catch (e: any) {
      console.log('bad page', this, e);
     }
   }
  
   public pdfBodyRef: ElementRef = null; // composant cible pour l'export PDF



  /** construction des rendus sous forme de string pour l'ensemble des champs */
  private feedWidgetLineAsString(wl: WidgetLine): void {
    if(!wl.byWidgetStringMap) {
      wl.byWidgetStringMap = new Map();
    } else {
      if(wl.byWidgetStringMap.has(this.uid)) {
        return;
      }
      
    }

    let res: string[] = [];
    let src: WidgetConfigurationItemObj[] = null;
    if(this.widgetKind === WIDGET_KIND.GRID || this.widgetKind === WIDGET_KIND.PIVOT) {
      src = [...this.context.r, ...this.context.d, ...this.context.c];
    } else { // tiles
      src = [...this.context.ff];
    }
    [...src].forEach(wcio => {
        // console.log('wcio', wcio);
        // const field : string = wcio.fdvf.split(WizConstants.FD_PARTS_SEPARATOR)[0];
        let key: FeiKeyDef = this.fei.keyMap.get(wcio.cod);
        let keyRes: string = null;
        
        // TODO pas les bool, obj & bin
        
        if(key.isFilterKey || key.isKey) {
          keyRes = BaselineEngine.getLabel(this.fei, wl, wcio.cod, wcio.fdvf || WizConstants.FIELD_LABEL);
        } else if(key.isMeasure) {
          keyRes = BaselineEngine.getValue(this.fei, wl, wl.cells?.get(wcio.cod));
        }
        
        if(!!keyRes) {
          res.push(keyRes.toUpperCase());
        }

    });

    wl.byWidgetStringMap.set(this.uid, res.join(WizConstants.KEY_SEPARATOR));
    //console.log('feedWidgetLineAsString', wl, res.join(WizConstants.KEY_SEPARATOR));
  }

  /**
   * utilisé pour les rendus de type WORKFLOW
   * @param params 
   * @returns 
   */
  public tooltipAccessor(params: ICellRendererParams): any {
    // console.log('tooltipAccessor', params);
    let rawRews: string = null;
    let result: string = '';
    if(params.value) {
      if(this.widgetGlobalKind === WIDGET_GLOBAL_KIND.FREEDATA) {
        rawRews = this.model.workflowEngine.valueFormatterFD(params);
      } else {
        rawRews = this.model.workflowEngine.valueFormatter(params);
      }
      try {
        let smallMess: any = JSON.parse(rawRews);
        if(smallMess?.keys) {
          let str: string = smallMess.keys.join("\n");
          result += str;
        }
      } catch(e: any) {

      }


    }

    return result;
  }

  public _previousSize: {width, height} = null;
  public onResize(width, height): void {
    this._previousSize = {width, height};
  }

  /**
   * il existe un filtre global obligatoire non renseigné
   */
  public mandatoryGlobalEmpty(): boolean {
    let emptyMand: boolean = false;
    try {
      let filter: IWidgetFilter; // les filtres qu'on va jouer
      // contrôle par filtre global obligatoire
      if(!!this.model.widgetEngine.globalFilter) { 
        let globalFilter: IWidget = this.model.widgetEngine.checkGlobalFilter();
        if(globalFilter?.filters) { // filtre actifs sur le global
          for (var i = 0, len = globalFilter.filters.size; i < len; i++) {
            filter = [...globalFilter.filters.values()][i];
            
            if(filter.mandatory && !filter.active) { // filtre global obligatoire sans valeur
              emptyMand = true;
              break;
            }
          }
      }
    }
    } catch(e: any) {

    }

    return emptyMand;
  }

    // patch pour le rendu des mesures de type FILTER 
  protected patchCrossFilterFormatter(params: any): string {
    console.log('TODO TODO patchCrossFilterFormatter', params);
    const fkd: FeiKeyDef = params.colDef.fkd;
    let result: string;

    const line: WidgetLine = params.data; // ligne source
    const colDef: WizColDef = params.colDef; // définition de la colonne
    const code: string = params.colDef.field; // champ mesure cible code#mode|fonction d'aggrégat
    // identifiant unique de la colonne
    const colId: number = params.column.instanceId;
    // valeur calculée par keyValueAccessor
    const val: string = params.value;
    // le format
    const format: string = params.colDef.dataformat;

    if(params.value === null || params.value === undefined) {
      return null;
    }

    // TODO prise du FD sur le settings
    // prise du FDV
    // puis collecte du champ label ou autre depuis settings
    // définitiond du type final ?


    if (this.widgetLineCellRendererExist(line, colId, val, format) ) { // déjà mémorisé
      result = this.getWidgetLineCellRendererValue(line, colId, val, format);
     
        // if(line.key === '152' && fkd.fullCode === 'M_COLLM#I') {
        //   console.log(' widgetLineCellRendererExist M_COLLM#I', val, result);
        // } 
      
      // console.log('patchFilterFormatter intercept', params.value, result);
      return result;
    } 

    try {
      let FDCode: string = (fkd.measure.settings as any).keys[0].FD;
      let FDVCode: string = val;
      if(FDCode && FDVCode) {
        let renderField: string = (fkd.measure.settings as any).keys[0].render || ViewData.FIELD_LABEL;
        let fdv: FreedataValueVO = this.model.freedataEngine.getFreedataValue(FDCode, FDVCode);
        if(!!fdv) {
          result = this.model.freedataEngine.getFDValue(fdv, renderField);
        }

        // if(line.key === '152' && fkd.fullCode === 'M_COLLM#I') {
        //   console.log(' not RendererExist M_COLLM#I', val, result);
        // } 

      }
    } catch (e:any) {
      // console.log('patchFilterFormatter', params.value);
    }

    
    // TODO TODO gestion pivot
    // let childPool: WidgetLine[];
    // if (!colDef.widget.pivotMode) { // mode simple (GRID)
    //   childPool = [line];
    // } else {
    //   childPool = this.getPool(line, params.column);
    // }

    // TODO

    // console.log('patchFilterFormatter', params.value, params.colDef.fkd, result);
    // on mémorise
    // console.log('patchFilterFormatter generate', params.value, result);
    this.setWidgetLineCellRendererValue(line, colId, val, format, result);
    return result;
  }

}
